import { Injectable, NgZone } from '@angular/core';
import { BehaviorSubject, Observable, ReplaySubject, Subject, firstValueFrom } from 'rxjs';
import { IncomeCallService } from 'src/app/_Common/_Services/income-call.service';
import { CallingService } from '../calling.service';
import { CubeAgentConfigService } from './cube-agent-config.service';
import { cubeDebugLog } from './cube-debug-log';
import {
  CubeConnectorUiStatus,
  CUBE_DEFAULT_DISPOSITION,
  describeMissingCubeCredentials,
  getCubeConnectorChildOrigin,
  getCubeIframePostMessageTarget,
  getCubeConnectorScriptUrl,
  getCubeWebphoneUrl,
  getCubeAutoAnswerEnabled,
  getCubeAutoTransferProfile,
  isCubeAgentConfigComplete,
  isCubeAutoTransferDummySession,
  isCubeAutoTransferSourceCampaign,
  isCubeWebphoneConnector,
} from './cube-dialer-mode';
import { CubeCallUiState, CubeDialerFlowService } from './cube-dialer-flow.service';
import {
  getCubeBreakConfig,
  isCubeBreakType,
  isCubeLoginBreak,
  resolveCubeBreakType,
} from './cube-break-types';
/** SDK early-return uses CubeConnector only. Iframe sendIframeAction bodies stay unchanged. */
import { shouldUseCubeSdk, getCubeBridgeIframeId } from '../cube-sdk/cube-sdk-mode';
import { CubeSdkService } from '../cube-sdk/cube-sdk.service';

type CubeWebphoneMessage = {
  source?: string;
  type?: string;
  payload?: unknown;
  reason?: string;
  agents?: CubeTransferAgent[];
};

export type CubeTransferAgent = {
  AgentId: string;
  Extension?: string;
  label?: string;
};

declare global {
  interface Window {
    CubeConnector?: new (
      iframeId: string,
      options?: Record<string, unknown>
    ) => CubeConnectorInstance;
  }
}

interface CubeConnectorInstance {
  setAgent(agent: Record<string, string>): void;
  setCallback(event: string, handler: (data: unknown) => void): void;
  login(): boolean;
  logout(): boolean;
  makeCall(number: string): boolean;
  answer(): boolean;
  hangup(): boolean;
  breakStart(breakInfo?: string): boolean;
  breakEnd(): boolean;
  dispose(disposition?: string): boolean;
  setAutoAnswer(autoAnswer: boolean): boolean;
  destroy(): void;
  isReady?(): boolean;
}

@Injectable({ providedIn: 'root' })
export class CubeConnectorCoreService {
  constructor(
    private ngZone: NgZone,
    private cubeAgentConfig: CubeAgentConfigService,
    private callingService: CallingService,
    private incomeCallService: IncomeCallService,
    private cubeDialerFlow: CubeDialerFlowService,
    private cubeSdk: CubeSdkService
  ) {}

  private connector: CubeConnectorInstance | null = null;
  private scriptLoaded = false;
  private agentLoggedIn = false;
  private loginInFlight = false;
  private softphoneReady = false;
  private loginFlightTimer: ReturnType<typeof setTimeout> | null = null;
  private loginAckTimer: ReturnType<typeof setTimeout> | null = null;
  private duplicateSessionRetryCount = 0;
  private readonly maxDuplicateSessionRetries = 2;
  private lastLoginError = '';
  private preLoginLogoutDone = false;
  private teardownDone = false;
  private callEndInProgress = false;
  private callDisposeInFlight = false;
  private callLegCancelled = false;
  private callControlInFlight = false;
  private messageBridgeInstalled = false;
  private connectTimer: ReturnType<typeof setTimeout> | null = null;
  private loginRequestTimer: ReturnType<typeof setTimeout> | null = null;
  private loginFallbackTimer: ReturnType<typeof setTimeout> | null = null;
  private loginStatePollTimer: ReturnType<typeof setInterval> | null = null;
  private lastLoginPushAt = 0;
  private uploadDialerWatchInstalled = false;
  private manualDialWatchInstalled = false;
  private campaignDialNudgeInFlight = false;
  private lastCampaignDialNudgeAt = 0;
  private pendingUploadLeads: Array<{ phoneNo?: string; fieldString2?: string; clientId?: string; fullName?: string; name?: string }> = [];
  private pendingCampaignMeta: Partial<CubeCallUiState> = {};
  private callSource: 'inbound' | 'campaign' | 'manual' | null = null;
  private cubeAwaitingAgentAnswer = false;
  private cubeTransferRouting = false;
  private transferArmSent = false;
  private cubeTransferDispatched = false;
  private dummyTransferUiReleased = false;
  private lastAutoAnswerEnabled: boolean | null = null;
  private agentAnsweredByUser = false;
  private cubeIframeAgentConnected = false;
  private cubeIframeIdleTicks = 0;
  private lastCallEndedAt = 0;
  private lastEndedCallPhone = '';
  private lastEndedCallHandle = '';
  private activeCallHandle = '';
  private readonly callEventCooldownMs = 4000;
  private conferenceThirdPartyPending = false;
  private transferAgentsResolve: ((agents: CubeTransferAgent[]) => void) | null = null;
  private transferAgentsTimer: ReturnType<typeof setTimeout> | null = null;
  private wrapUpResolve: (() => void) | null = null;
  private wrapUpTimer: ReturnType<typeof setTimeout> | null = null;
  private freemeResolve: (() => void) | null = null;
  private freemeTimer: ReturnType<typeof setTimeout> | null = null;
  private autoClearBreakUntil = 0;
  private userBreakArmed = false;
  private callerNameByPhone = new Map<string, string>();
  private autoAnswerPromoteTimer: ReturnType<typeof setTimeout> | null = null;
  private transferResultWait: ((ok: boolean) => void) | null = null;
  private transferResultTimer: ReturnType<typeof setTimeout> | null = null;
  private cubeTransferInFlight = false;
  private sdkWidgetWired = false;
  private sdkBreakStartAt = 0;
  private cubeWrapUpPending = false;
  private callLegPhase: 'idle' | 'active' | 'wrap_up' = 'idle';
  private callLegEverConnected = false;
  private callLegId = '';
  private outboundDialSent = false;
  private callLegStartedAt = 0;

  private cubeLog(method: string, detail?: Record<string, unknown>): void {
    cubeDebugLog('Connector', method, detail);
  }
  private readonly boundMessageBridge = (event: MessageEvent) => this.handleWebphoneMessage(event);

  readonly status$ = new BehaviorSubject<CubeConnectorUiStatus>('initializing');
  readonly callState$ = new BehaviorSubject<CubeCallUiState | null>(null);
  readonly loginState$ = new BehaviorSubject<boolean>(false);
  readonly ready$ = new ReplaySubject<unknown>(1);
  readonly loggedIn$ = new ReplaySubject<unknown>(1);
  readonly loginFailed$ = new ReplaySubject<unknown>(1);
  readonly loggedOut$ = new ReplaySubject<unknown>(1);
  readonly inboundCall$ = new Subject<unknown>();
  readonly callAnswered$ = new Subject<unknown>();
  readonly callEnded$ = new Subject<unknown>();
  readonly wrapUpStarted$ = new Subject<unknown>();
  readonly campaignCustomerAnswered$ = new Subject<unknown>();
  readonly transferCompleted$ = new Subject<unknown>();
  readonly breakStarted$ = new Subject<unknown>();
  readonly breakEnded$ = new Subject<unknown>();
  readonly error$ = new Subject<string>();
  readonly iframeCallControlsActive$ = new BehaviorSubject<boolean>(false);

  getCurrentStatus(): CubeConnectorUiStatus {
    return this.status$.getValue();
  }

  isAwaitingAgentAnswer(): boolean {
    return this.cubeAwaitingAgentAnswer;
  }

  clearRingState(): void {
    this.cubeAwaitingAgentAnswer = false;
    this.agentAnsweredByUser = false;
    this.cubeIframeAgentConnected = false;
    this.cubeIframeIdleTicks = 0;
    this.clearAutoAnswerPromoteTimer();
  }

  setTransferRouting(active: boolean): void {
    this.cubeTransferRouting = !!active;
    if (active) {
      this.clearAutoAnswerPromoteTimer();
      if (!this.transferArmSent) {
        this.armTransferRouting();
      } else {
        this.setAutoAnswer(false);
      }
    } else {
      this.transferArmSent = false;
      this.sendIframeAction('CLEAR_TRANSFER_ARM', {});
      if (getCubeAutoAnswerEnabled() && !this.shouldDisableAutoAnswerForTransfer()) {
        this.setAutoAnswer(true);
      }
    }
  }

  armTransferRouting(): void {
    if (this.transferArmSent) {
      return;
    }
    this.transferArmSent = true;
    this.sendIframeAction('ARM_TRANSFER', {});
    this.setAutoAnswer(false);
  }

  isTransferRouting(): boolean {
    return this.cubeTransferRouting;
  }

  isCallSessionActive(): boolean {
    if (this.callEndInProgress || this.callLegCancelled) {
      return false;
    }
    if (this.callLegPhase === 'active') {
      return true;
    }
    const status = this.callState$.getValue()?.Status;
    if (status === 'Idle' || status === 'Disconnected') {
      return false;
    }
    return (
      status === 'CALLING' ||
      status === 'CONNECTED' ||
      this.cubeAwaitingAgentAnswer ||
      this.cubeIframeAgentConnected
    );
  }

  isCallLegActive(): boolean {
    return this.callLegPhase === 'active';
  }

  hasActiveOrWrapUpLeg(): boolean {
    return this.callLegPhase === 'active' || this.callLegPhase === 'wrap_up' || this.cubeWrapUpPending;
  }

  wasCallConnected(): boolean {
    return this.callLegEverConnected;
  }

  isRingingUnanswered(): boolean {
    if (this.callLegEverConnected) {
      return false;
    }
    const status = this.callState$.getValue()?.Status;
    return (
      this.callLegPhase === 'active' &&
      (status === 'CALLING' || this.cubeAwaitingAgentAnswer || this.isManualDialerCall())
    );
  }

  canShowDisposition(reason?: string): boolean {
    const r = String(reason || '').toLowerCase();
    const agentEnded = r === 'agent_hangup' || r === 'ui_arm';
    if (this.callLegEverConnected) {
      return true;
    }
    if (agentEnded) {
      if (this.outboundDialSent || this.callLegPhase === 'active' || this.callLegPhase === 'wrap_up') {
        return true;
      }
      const status = this.callState$.getValue()?.Status;
      if (status === 'CALLING' || status === 'CONNECTED' || status === 'Disconnected') {
        return true;
      }
    }
    return false;
  }

  private beginCallLeg(source: 'inbound' | 'campaign' | 'manual'): void {
    if (this.callLegPhase === 'wrap_up' || this.cubeWrapUpPending) {
      this.cubeWrapUpPending = false;
      this.callDisposeInFlight = false;
    }
    this.callLegPhase = 'active';
    this.callLegEverConnected = false;
    this.outboundDialSent = false;
    this.callLegId = `${source}-${Date.now()}`;
    this.callLegStartedAt = Date.now();
    this.callSource = source;
    this.callEndInProgress = false;
    this.callLegCancelled = false;
    this.cubeWrapUpPending = false;
    this.cubeIframeIdleTicks = 0;
    this.cubeIframeAgentConnected = false;
    this.agentAnsweredByUser = false;
    this.cubeLog('beginCallLeg', { source, callLegId: this.callLegId });
  }

  private resolveCallLegSource(
    fallback: 'inbound' | 'campaign' | 'manual' = 'inbound'
  ): 'inbound' | 'campaign' | 'manual' {
    if (this.callSource === 'campaign' || this.callSource === 'manual' || this.callSource === 'inbound') {
      return this.callSource;
    }
    if (this.pendingUploadLeads.length > 0) {
      return 'campaign';
    }
    if (this.isManualDialerCall()) {
      return 'manual';
    }
    return fallback;
  }

  private markLegConnected(): void {
    const keepAnswered = this.agentAnsweredByUser;
    const keepIframe = this.cubeIframeAgentConnected;
    const keepAwaiting = this.cubeAwaitingAgentAnswer;
    if (this.callLegPhase !== 'active') {
      this.beginCallLeg(this.resolveCallLegSource('inbound'));
    }
    this.agentAnsweredByUser = keepAnswered;
    this.cubeIframeAgentConnected = keepIframe;
    this.cubeAwaitingAgentAnswer = keepAwaiting;
    this.callLegEverConnected = true;
    this.cubeIframeIdleTicks = 0;
  }

  private endCallLegToIdle(): void {
    this.callLegPhase = 'idle';
    this.callLegEverConnected = false;
    this.outboundDialSent = false;
    this.callLegId = '';
    this.callLegStartedAt = 0;
  }

  isCallCleanupActive(): boolean {
    return this.callEndInProgress || this.callLegCancelled;
  }

  reconcileStaleCall(reason = 'reconcile'): void {
    if (this.callLegPhase === 'active' || this.callLegPhase === 'wrap_up' || this.cubeWrapUpPending) {
      this.cubeLog('reconcileStaleCall', { skip: 'leg_active_or_wrap', reason, phase: this.callLegPhase });
      return;
    }
    const state = this.callState$.getValue();
    const uiStatus = this.status$.getValue();
    const residual =
      !!state?.phoneNo ||
      !!state?.customerAnswered ||
      this.cubeAwaitingAgentAnswer ||
      this.cubeTransferRouting ||
      this.cubeIframeAgentConnected ||
      uiStatus === 'on_call';
    this.cubeLog('reconcileStaleCall', {
      reason,
      residual,
      status: state?.Status,
      uiStatus,
      iframeLive: this.cubeIframeAgentConnected,
    });
    if (residual) {
      this.forceCallCleanup(reason);
      return;
    }
    this.clearRingState();
    if (this.agentLoggedIn && uiStatus !== 'ready' && uiStatus !== 'on_break') {
      this.setUiStatus('ready');
    }
  }

  hardResetCallState(source = 'reset'): void {
    this.cubeLog('hardResetCallState', {
      source,
      prevStatus: this.callState$.getValue()?.Status,
      prevPhone: this.callState$.getValue()?.phoneNo || '',
      uiStatus: this.status$.getValue(),
    });
    this.cancelTransferResultWait(false);
    this.clearConnectTimer();
    this.clearAutoAnswerPromoteTimer();
    this.cubeTransferRouting = false;
    this.transferArmSent = false;
    this.cubeTransferDispatched = false;
    this.cubeTransferInFlight = false;
    this.cubeAwaitingAgentAnswer = false;
    this.agentAnsweredByUser = false;
    this.cubeIframeAgentConnected = false;
    this.cubeIframeIdleTicks = 0;
    this.callSource = null;
    this.activeCallHandle = '';
    this.conferenceThirdPartyPending = false;
    this.dummyTransferUiReleased = false;
    this.pendingCampaignMeta = {};
    this.callEndInProgress = false;
    this.callLegCancelled = false;
    this.cubeWrapUpPending = false;
    this.endCallLegToIdle();
    this.clearCallSessionCaches();
    this.setIframeCallControlsActive(false);
    const cur = this.callState$.getValue();
    const alreadyIdle =
      cur?.Status === 'Idle' &&
      !cur?.phoneNo &&
      !cur?.fullName &&
      !this.cubeIframeAgentConnected;
    if (!alreadyIdle) {
      this.ngZone.run(() => {
        this.callState$.next({
          ...(cur || { callingType: 'cube' }),
          callingType: 'cube',
          Status: 'Idle',
          holdCall: false,
          customerAnswered: false,
          phoneNo: '',
          dueId: undefined,
          clientId: undefined,
          fullName: undefined,
          pauseCall: false,
          breakValue: '',
          callDirection: undefined,
          campaignName: undefined,
        });
      });
    }
    if (this.agentLoggedIn) {
      this.ngZone.run(() => this.status$.next('ready'));
    }
    this.incomeCallService.removeCallingRes();
    localStorage.removeItem('incomingCall');
    this.cubeLog('hardResetCallState', { source, done: true });
  }

  isTransferBlocked(): boolean {
    return this.callEndInProgress || this.callLegCancelled;
  }

  isTransferInFlight(): boolean {
    return this.cubeTransferInFlight;
  }

  isWrapUpPending(): boolean {
    return this.cubeWrapUpPending;
  }

  getActiveCallSource(): 'inbound' | 'campaign' | 'manual' | null {
    return this.callSource;
  }

  getActiveCallDirection(): 'campaign' | 'manual' | 'inbound' | '' {
    const dir = String(this.callState$.getValue()?.callDirection || '').toLowerCase();
    if (dir === 'campaign' || dir === 'manual' || dir === 'inbound') {
      return dir as 'campaign' | 'manual' | 'inbound';
    }
    if (this.callSource === 'campaign' || this.callSource === 'manual' || this.callSource === 'inbound') {
      return this.callSource;
    }
    return '';
  }

  isCampaignDialerCall(): boolean {
    if (this.callSource === 'manual') {
      return false;
    }
    const dir = this.getActiveCallDirection();
    if (dir === 'manual') {
      return false;
    }
    return this.callSource === 'campaign' || dir === 'campaign';
  }

  isManualDialerCall(): boolean {
    return this.callSource === 'manual' || this.getActiveCallDirection() === 'manual';
  }

  isAgentOriginatedOutbound(): boolean {
    return this.outboundDialSent;
  }

  private setCallSourceCampaignIfAllowed(): void {
    if (this.isManualDialerCall()) {
      return;
    }
    this.callSource = 'campaign';
  }

  clearCallSessionCaches(): void {
    this.cubeLog('clearCallSessionCaches', {});
    this.lastCallEndedAt = 0;
    this.lastEndedCallPhone = '';
    this.lastEndedCallHandle = '';
    this.activeCallHandle = '';
    this.pendingCampaignMeta = {};
    this.cubeIframeIdleTicks = 0;
    this.cubeIframeAgentConnected = false;
    this.cubeAwaitingAgentAnswer = false;
    this.agentAnsweredByUser = false;
    this.cubeTransferRouting = false;
    this.transferArmSent = false;
    this.cubeTransferDispatched = false;
    this.cubeTransferInFlight = false;
    this.conferenceThirdPartyPending = false;
    this.dummyTransferUiReleased = false;
    this.callSource = null;
    try {
      localStorage.removeItem('incomingCall');
      localStorage.removeItem('clientId');
    } catch (_e) {}
  }

  async submitWrapUpDisposition(
    disposition: string,
    comment = ''
  ): Promise<{ success: boolean; message?: string }> {
    const disp = String(disposition || '').trim() || CUBE_DEFAULT_DISPOSITION;
    if (!this.cubeWrapUpPending && this.callLegPhase !== 'wrap_up') {
      return { success: false, message: 'No wrap-up active' };
    }
    this.cubeWrapUpPending = true;
    this.callLegPhase = 'wrap_up';
    this.cubeLog('submitWrapUpDisposition', { disp, comment: comment ? '(set)' : '' });
    try {
      if (shouldUseCubeSdk()) {
        // Cube stays in WRAP until CubeConnector.dispose() — iframe DISPOSE_CALL is ignored.
        if (!this.cubeSdk.dispose(disp, null, '0')) {
          return { success: false, message: 'Failed to dispose call' };
        }
      } else {
        this.completeWrapUp(disp);
        await this.waitForWrapUp(1500);
      }
      const state = this.callState$.getValue();
      try {
        await Promise.race([
          firstValueFrom(
            this.callingService.disposeCubeCallRaphsody({
              clientId: state?.clientId || '',
              Disposition: disp,
            })
          ),
          new Promise((_, reject) => setTimeout(() => reject(new Error('timeout')), 4000)),
        ]);
      } catch (_e) {}
      void this.bootstrapRestAuthId();
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : 'Dispose failed';
      this.cubeLog('submitWrapUpDisposition', { error: msg });
      return { success: false, message: msg };
    }
    this.cubeWrapUpPending = false;
    this.endCallLegToIdle();
    this.notifyCallEnded('disposition');
    return { success: true };
  }

  isCubeTransferDispatched(): boolean {
    return this.cubeTransferDispatched;
  }

  clearTransferDispatchForRetry(): void {
    this.cubeTransferDispatched = false;
    this.cubeTransferRouting = false;
  }

  isCubeTransferConfirmedForRelease(data?: unknown): boolean {
    const payload = data as { ok?: boolean; queued?: boolean } | undefined;
    return !!(payload?.ok && !payload?.queued);
  }

  resolveAwaitingForConnectedState(): void {
    if (this.shouldDisableAutoAnswerForTransfer()) {
      return;
    }
    if (!this.cubeAwaitingAgentAnswer) {
      return;
    }
    this.cubeAwaitingAgentAnswer = false;
    this.agentAnsweredByUser = true;
  }

  releaseAfterBlindTransfer(): void {
    if (this.dummyTransferUiReleased) {
      this.cubeLog('releaseAfterBlindTransfer', { skip: 'already_released' });
      return;
    }
    this.cubeLog('releaseAfterBlindTransfer', { start: true });
    this.dummyTransferUiReleased = true;
    const endedPhone = String(this.callState$.getValue()?.phoneNo || '').replace(/\D/g, '').slice(-10);
    this.lastCallEndedAt = Date.now();
    this.lastEndedCallPhone = endedPhone;
    this.lastEndedCallHandle = this.activeCallHandle;
    this.cubeTransferRouting = false;
    this.transferArmSent = false;
    this.cubeTransferDispatched = false;
    this.cubeAwaitingAgentAnswer = false;
    this.agentAnsweredByUser = false;
    this.cubeIframeAgentConnected = false;
    this.sendIframeAction('CLEAR_TRANSFER_ARM', {});
    this.sendIframeAction('RELEASE_DUMMY_UI', {});
    this.setIframeCallControlsActive(false);
    this.applyConfiguredAutoAnswer();
    this.ngZone.run(() => {
      this.publishCallState({
        Status: 'Idle',
        holdCall: false,
        customerAnswered: false,
        phoneNo: '',
        fullName: '',
      });
      if (this.agentLoggedIn) {
        this.status$.next('ready');
      }
      this.emit(this.callEnded$, {
        type: 'CALL_ENDED',
        reason: 'blind_transfer',
        via: 'release_dummy',
      });
    });
  }

  private notifyCallEnded(reason = 'remote'): void {
    this.cubeLog('notifyCallEnded', { reason });
    this.hardResetCallState(reason);
    this.broadcastCallEnd();
    this.emit(this.callEnded$, { type: 'CALL_ENDED', reason });
  }

  isAnswerInFlight(): boolean {
    return this.agentAnsweredByUser && this.callState$.getValue()?.Status === 'CALLING';
  }

  isCallConnected(): boolean {
    return this.callState$.getValue()?.Status === 'CONNECTED';
  }

  isCubeIframeAgentConnected(): boolean {
    return this.cubeIframeAgentConnected;
  }

  isIframeCallLive(): boolean {
    return this.cubeIframeAgentConnected && this.isCallSessionActive();
  }

  flushIframeCallState(data?: unknown, live = false): void {
    this.cubeLog('flushIframeCallState', { live });
    if (live) {
      return;
    }
    const status = this.callState$.getValue()?.Status;
    const wasLive =
      status === 'CONNECTED' ||
      status === 'CALLING' ||
      this.status$.getValue() === 'on_call';
    if (!wasLive) {
      this.cubeIframeAgentConnected = false;
      this.cubeIframeIdleTicks = 0;
      this.cubeAwaitingAgentAnswer = false;
      this.clearRingState();
      return;
    }
    if (this.shouldIgnoreIframeIdleFlush()) {
      this.cubeLog('flushIframeCallState', {
        skip: 'ringing_or_manual_pending',
        status,
        callSource: this.callSource,
        iframeLive: this.cubeIframeAgentConnected,
      });
      return;
    }
    this.handleRemoteCallEnd({
      ...(typeof data === 'object' && data ? data : {}),
      type: 'CALL_ENDED',
      reason: 'iframe_idle_flush',
      via: 'callwatch',
    });
    this.cubeIframeAgentConnected = false;
    this.cubeIframeIdleTicks = 0;
    this.cubeAwaitingAgentAnswer = false;
  }

  private shouldIgnoreIframeIdleFlush(): boolean {
    if (this.cubeWrapUpPending || this.callEndInProgress || this.callDisposeInFlight) {
      return true;
    }
    if (this.callLegPhase === 'active' || this.callLegPhase === 'wrap_up') {
      return true;
    }
    const status = this.callState$.getValue()?.Status;
    if (status === 'CALLING') {
      return true;
    }
    if (status === 'CONNECTED' || this.cubeIframeAgentConnected || this.agentAnsweredByUser) {
      return true;
    }
    if (this.isManualDialerCall()) {
      return true;
    }
    if (this.cubeAwaitingAgentAnswer) {
      return true;
    }
    return false;
  }

  isConferenceMergeReady(): boolean {
    return this.conferenceThirdPartyPending;
  }

  isAgentOnBreak(): boolean {
    return (
      this.getCurrentStatus() === 'on_break' ||
      this.callState$.getValue()?.Status === 'Break' ||
      !!this.callState$.getValue()?.pauseCall
    );
  }

  isCallOnHold(): boolean {
    return !!this.callState$.getValue()?.holdCall;
  }

  fetchTransferAgents(timeoutMs = 10000): Promise<CubeTransferAgent[]> {
    return this.fetchTransferAgentsViaRest().then((agents) => {
      if (agents.length) {
        return agents;
      }
      return this.fetchTransferAgentsFromIframe(timeoutMs);
    });
  }

  private async fetchTransferAgentsViaRest(): Promise<CubeTransferAgent[]> {
    try {
      const agent = await this.cubeAgentConfig.ensureLoaded();
      const campaignId = String(
        agent?.campaignId ||
        this.callState$.getValue()?.['campaignId'] ||
        ''
      ).trim();
      const res = await firstValueFrom(
        this.callingService.getCubeCampaignAgents(campaignId || undefined)
      ) as { agents?: CubeTransferAgent[]; resultShort?: string };
      if (String(res?.resultShort || '').toLowerCase() === 'failure') {
        return [];
      }
      return this.normalizeTransferAgentRows(res?.agents);
    } catch (_e) {
      return [];
    }
  }

  private fetchTransferAgentsFromIframe(timeoutMs = 10000): Promise<CubeTransferAgent[]> {
    return new Promise((resolve) => {
      if (this.transferAgentsTimer) {
        clearTimeout(this.transferAgentsTimer);
      }
      const done = (agents: CubeTransferAgent[]) => {
        if (this.transferAgentsTimer) {
          clearTimeout(this.transferAgentsTimer);
          this.transferAgentsTimer = null;
        }
        this.transferAgentsResolve = null;
        resolve(agents);
      };
      this.transferAgentsResolve = done;
      if (!this.sendIframeAction('LIST_TRANSFER_AGENTS')) {
        done([]);
        return;
      }
      this.transferAgentsTimer = setTimeout(() => {
        if (this.transferAgentsResolve) {
          done([]);
        }
      }, timeoutMs);
    });
  }

  private normalizeTransferAgentRows(agents: CubeTransferAgent[] | undefined): CubeTransferAgent[] {
    const seen = new Set<string>();
    const out: CubeTransferAgent[] = [];
    for (const a of agents || []) {
      const id = String(a?.AgentId || '').trim();
      if (!id) {
        continue;
      }
      const key = id.toLowerCase();
      if (seen.has(key)) {
        continue;
      }
      seen.add(key);
      out.push({
        AgentId: id,
        Extension: String(a.Extension || '').trim(),
        label: String(a.label || (a as { Name?: string }).Name || id).trim() || id,
      });
    }
    out.sort((a, b) => a.AgentId.localeCompare(b.AgentId, undefined, { sensitivity: 'base' }));
    return out;
  }

  private resolveTransferAgents(data: unknown): void {
    if (!this.transferAgentsResolve) {
      return;
    }
    const root = (data as { agents?: CubeTransferAgent[] }) || {};
    const agents = this.normalizeTransferAgentRows(root.agents);
    const done = this.transferAgentsResolve;
    if (this.transferAgentsTimer) {
      clearTimeout(this.transferAgentsTimer);
      this.transferAgentsTimer = null;
    }
    this.transferAgentsResolve = null;
    done(agents);
  }

  private setUiStatus(status: CubeConnectorUiStatus): void {
    this.ngZone.run(() => this.status$.next(status));
  }

  private loadScript(src: string): Promise<void> {
    if (document.querySelector(`script[src="${src}"]`)) {
      this.scriptLoaded = true;
      return Promise.resolve();
    }
    return new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = src;
      script.async = true;
      script.onload = () => resolve();
      script.onerror = () => reject(new Error(`Failed to load ${src}`));
      document.body.appendChild(script);
    });
  }

  async ensureScript(): Promise<void> {
    if (this.scriptLoaded) return;
    await this.loadScript(getCubeConnectorScriptUrl());
    this.scriptLoaded = true;
  }

  private waitForIframe(id: string, timeoutMs = 10000): Promise<HTMLIFrameElement> {
    return new Promise((resolve, reject) => {
      const started = Date.now();
      const check = () => {
        const el = document.getElementById(id) as HTMLIFrameElement | null;
        if (el) {
          resolve(el);
          return;
        }
        if (Date.now() - started > timeoutMs) {
          reject(new Error(`Iframe #${id} not found in DOM`));
          return;
        }
        requestAnimationFrame(check);
      };
      check();
    });
  }

  private clearLoginFlightTimer(): void {
    if (this.loginFlightTimer) {
      clearTimeout(this.loginFlightTimer);
      this.loginFlightTimer = null;
    }
  }

  private clearLoginAckTimer(): void {
    if (this.loginAckTimer) {
      clearTimeout(this.loginAckTimer);
      this.loginAckTimer = null;
    }
  }

  private clearConnectTimer(): void {
    if (this.connectTimer) {
      clearTimeout(this.connectTimer);
      this.connectTimer = null;
    }
  }

  private clearLoginRequestTimer(): void {
    if (this.loginRequestTimer) {
      clearTimeout(this.loginRequestTimer);
      this.loginRequestTimer = null;
    }
  }

  private clearLoginFallbackTimer(): void {
    if (this.loginFallbackTimer) {
      clearTimeout(this.loginFallbackTimer);
      this.loginFallbackTimer = null;
    }
  }

  private clearLoginStatePoll(): void {
    if (this.loginStatePollTimer) {
      clearInterval(this.loginStatePollTimer);
      this.loginStatePollTimer = null;
    }
  }

  private startLoginStatePoll(): void {
    if (this.loginStatePollTimer || this.agentLoggedIn) {
      return;
    }
    this.pingIframeLoginState();
    this.loginStatePollTimer = setInterval(() => {
      if (this.agentLoggedIn) {
        this.clearLoginStatePoll();
        return;
      }
      this.pingIframeLoginState();
    }, 2000);
  }

  private pingIframeLoginState(): void {
    this.postToCubeIframe({ source: 'SF_ADAPTER', type: 'PING_LOGIN_STATE' });
  }

  requestLoginStatePing(): void {
    if (shouldUseCubeSdk()) {
      return;
    }
    this.pingIframeLoginState();
    if (!this.agentLoggedIn) {
      this.startLoginStatePoll();
    }
  }

  private scheduleLoginFallback(): void {
    if (this.loginFallbackTimer || this.agentLoggedIn) {
      return;
    }
    this.startLoginStatePoll();
    [3000, 7000].forEach((delay) => {
      setTimeout(() => {
        if (!this.agentLoggedIn && !this.loginInFlight) {
          void this.sendLoginToIframe(true, true);
        }
      }, delay);
    });
  }

  requestLogin(immediate = false): void {
    if (shouldUseCubeSdk()) {
      void this.loginViaSdk(immediate);
      return;
    }
    if (this.agentLoggedIn) {
      return;
    }
    this.clearLoginRequestTimer();
    const delay = immediate ? 0 : 800;
    this.loginRequestTimer = setTimeout(() => {
      this.loginRequestTimer = null;
      if (!this.agentLoggedIn) {
        void this.sendLoginToIframe(true, true);
      }
    }, delay);
  }

  private extractCallMeta(data: unknown): Partial<CubeCallUiState> {
    const enriched = this.cubeDialerFlow.enrichCallDisplayData(data);
    const leadMeta = this.resolvePendingLeadMeta(enriched.phoneNo);
    const phone = enriched.phoneNo || leadMeta.phoneNo;
    const cachedName = phone ? this.callerNameByPhone.get(String(phone).replace(/\D/g, '').slice(-10)) : '';
    const fullName = enriched.fullName || leadMeta.fullName || cachedName || '';
    if (phone && fullName) {
      this.rememberCallerName(phone, fullName);
    }
    return {
      ...leadMeta,
      ...(enriched.phoneNo ? { phoneNo: enriched.phoneNo } : {}),
      ...(enriched.clientId ? { clientId: enriched.clientId } : {}),
      ...(enriched.dueId ? { dueId: enriched.dueId } : {}),
      ...(fullName ? { fullName } : {}),
    };
  }

  private rememberCallerName(phone?: string, fullName?: string): void {
    const key = String(phone || '').replace(/\D/g, '').slice(-10);
    const name = String(fullName || '').trim();
    if (key.length >= 10 && name && !/^unknown$/i.test(name)) {
      this.callerNameByPhone.set(key, name);
    }
  }

  private applyCachedCallerName(patch: Partial<CubeCallUiState>): Partial<CubeCallUiState> {
    const phone = String(patch.phoneNo || this.callState$.getValue()?.phoneNo || '')
      .replace(/\D/g, '')
      .slice(-10);
    if (!phone || patch.fullName) {
      return patch;
    }
    const cached = this.callerNameByPhone.get(phone);
    return cached ? { ...patch, fullName: cached } : patch;
  }

  private resolvePendingLeadMeta(phoneNo?: string): Partial<CubeCallUiState> {
    if (!this.pendingUploadLeads.length) {
      return {};
    }
    const normalized = String(phoneNo || '').replace(/\D/g, '').slice(-10);
    let lead =
      normalized.length >= 10
        ? this.pendingUploadLeads.find((item) => {
            const leadPhone = String(item?.phoneNo || '')
              .replace(/\D/g, '')
              .slice(-10);
            return leadPhone.length >= 10 && leadPhone === normalized;
          })
        : undefined;
    if (!lead) {
      lead = this.pendingUploadLeads[0];
    }
    if (!lead) {
      return {};
    }
    const phone = (normalized.length >= 10 ? normalized : '') || String(lead.phoneNo || '').replace(/\D/g, '').slice(-10);
    const clientId = String(lead.fieldString2 || lead.clientId || '').trim();
    const duePart = clientId.includes(':') ? clientId.split(':').pop() : '';
    const fullName = String(lead.fullName || lead.name || '').trim();
    return {
      ...(phone ? { phoneNo: phone } : {}),
      ...(clientId ? { clientId } : {}),
      ...(duePart ? { dueId: /^\d+$/.test(duePart) ? Number(duePart) : duePart } : {}),
      ...(fullName ? { fullName } : {}),
    };
  }

  private beginCallTeardown(): void {
    this.callLegCancelled = true;
    this.agentAnsweredByUser = false;
    this.cubeAwaitingAgentAnswer = false;
  }

  private extractPhoneFromEvent(data: unknown): string {
    const raw =
      (data as { phoneNo?: string; phoneNumber?: string })?.phoneNo ||
      (data as { phoneNumber?: string })?.phoneNumber ||
      '';
    return String(raw).replace(/\D/g, '').slice(-10);
  }

  private extractCallHandle(data?: unknown): string {
    const raw =
      (data as { callHandle?: string | number })?.callHandle ||
      (data as { handle?: string | number })?.handle ||
      (data as { nCallRefNo?: string | number })?.nCallRefNo ||
      '';
    const handle = String(raw || '').trim();
    return handle && handle !== '0' ? handle : '';
  }

  private shouldIgnoreStaleCallEvent(data?: unknown): boolean {
    if (this.callEndInProgress || this.callLegCancelled) {
      return true;
    }
    const status = this.callState$.getValue()?.Status;
    if (status === 'CALLING' || status === 'CONNECTED') {
      return false;
    }
    if (!this.lastCallEndedAt || Date.now() - this.lastCallEndedAt >= this.callEventCooldownMs) {
      return false;
    }
    const phone = this.extractPhoneFromEvent(data);
    const handle = this.extractCallHandle(data);
    if (handle && this.lastEndedCallHandle && handle === this.lastEndedCallHandle) {
      return true;
    }
    if (phone && phone.length >= 10 && phone === this.lastEndedCallPhone) {
      return true;
    }
    return false;
  }

  private alignCallLegForProgress(data?: unknown): boolean {
    const phone = this.extractPhoneFromEvent(data);
    const handle = this.extractCallHandle(data);
    const cur = this.callState$.getValue();
    const curPhone = String(cur?.phoneNo || '').replace(/\D/g, '').slice(-10);
    const curStatus = cur?.Status;
    if (
      (this.callLegPhase === 'wrap_up' || this.cubeWrapUpPending) &&
      !this.callLegEverConnected &&
      !this.cubeIframeAgentConnected
    ) {
      this.resumeCallFromPrematureWrapUp('CALLING', {
        phoneNo: phone || curPhone || cur?.phoneNo,
        fullName: cur?.fullName,
        dueId: cur?.dueId,
        clientId: cur?.clientId,
      });
      if (handle) {
        this.activeCallHandle = handle;
      }
      return true;
    }
    const newLeg =
      (handle && this.activeCallHandle && handle !== this.activeCallHandle) ||
      (phone.length >= 10 && curPhone.length >= 10 && phone !== curPhone) ||
      (phone.length >= 10 &&
        (curStatus === 'Idle' || curStatus === 'Disconnected' || !curStatus));
    if (!newLeg) {
      if (handle) {
        this.activeCallHandle = handle;
      }
      if (this.callLegPhase === 'idle' && (curStatus === 'CALLING' || curStatus === 'CONNECTED' || !!phone)) {
        this.beginCallLeg(this.callSource || (this.pendingUploadLeads.length ? 'campaign' : 'manual'));
      }
      return true;
    }
    if (this.callLegPhase === 'wrap_up' || this.cubeWrapUpPending) {
      return false;
    }
    if (this.callEndInProgress && Date.now() - this.lastCallEndedAt < 800) {
      return false;
    }
    this.callEndInProgress = false;
    this.callLegCancelled = false;
    this.cubeTransferRouting = false;
    this.transferArmSent = false;
    this.cubeTransferDispatched = false;
    this.dummyTransferUiReleased = false;
    this.cubeIframeAgentConnected = false;
    this.cubeIframeIdleTicks = 0;
    this.activeCallHandle = handle || '';
    this.beginCallLeg(this.callSource || (this.pendingUploadLeads.length ? 'campaign' : 'inbound'));
    return true;
  }

  private tearDownCallLeg(): void {
    this.sendIframeAction('HANGUP_CALL');
  }

  publishCallState(patch: Partial<CubeCallUiState>): void {
    if (this.callLegCancelled && patch.Status === 'CONNECTED') {
      return;
    }
    if (
      this.callEndInProgress &&
      (patch.Status === 'CONNECTED' || patch.Status === 'CALLING')
    ) {
      return;
    }
    patch = this.applyCachedCallerName(patch);
    if (patch.phoneNo && patch.fullName) {
      this.rememberCallerName(patch.phoneNo, patch.fullName);
    }
    if (this.callLegPhase === 'active') {
      if (patch.Status === 'Idle') {
        this.cubeLog('publishCallState', { skip: 'idle_while_active' });
        return;
      }
      if (patch.Status === 'Disconnected' && !this.cubeWrapUpPending) {
        this.cubeLog('publishCallState', { skip: 'disconnect_while_active' });
        return;
      }
    }
    if (this.callLegPhase === 'wrap_up') {
      if (patch.Status === 'Idle') {
        this.cubeLog('publishCallState', { skip: 'idle_during_wrap_up' });
        return;
      }
    }
    if (
      this.callLegPhase === 'wrap_up' &&
      (patch.Status === 'CALLING' || patch.Status === 'CONNECTED')
    ) {
      if (this.callLegCancelled) {
        this.cubeLog('publishCallState', { skip: 'progress_after_agent_hangup', status: patch.Status });
        return;
      }
      this.resumeCallFromPrematureWrapUp(patch.Status, patch);
      return;
    }
    if (patch.Status === 'CONNECTED' && !this.canPromoteToConnected() && !this.cubeIframeAgentConnected) {
      patch = { ...patch, Status: 'CALLING' };
    }
    const current = this.callState$.getValue();
    if (
      patch.Status === 'CALLING' &&
      (current?.Status === 'CONNECTED' || this.cubeIframeAgentConnected || this.callLegEverConnected)
    ) {
      patch = { ...patch, Status: 'CONNECTED' };
    }
    const clearingIdle = patch.Status === 'Idle' && !patch.phoneNo && !patch.fullName;
    const next: CubeCallUiState = {
      ...(current || { callingType: 'cube', Status: 'Idle' }),
      ...patch,
      callingType: 'cube',
    };
    if (!clearingIdle) {
      if (!next.phoneNo && current?.phoneNo) {
        next.phoneNo = current.phoneNo;
        next.phoneNumber = current.phoneNumber || current.phoneNo;
      }
      if (!next.fullName && current?.fullName) {
        next.fullName = current.fullName;
      }
      if (!next.dueId && current?.dueId) {
        next.dueId = current.dueId;
      }
      if (!next.clientId && current?.clientId) {
        next.clientId = current.clientId;
      }
    }
    if (next.Status === 'CONNECTED') {
      this.cubeAwaitingAgentAnswer = false;
      if (this.callLegPhase === 'active') {
        this.callLegEverConnected = true;
      } else {
        this.markLegConnected();
      }
    }
    this.ngZone.run(() => this.callState$.next(next));
  }

  private scheduleAnswerConnectFallback(): void {
    this.clearConnectTimer();
    if (this.callSource === 'manual') {
      return;
    }
    this.connectTimer = setTimeout(() => {
      if (this.callLegCancelled || this.callEndInProgress || this.callDisposeInFlight) {
        return;
      }
      const state = this.callState$.getValue();
      if (
        (this.agentAnsweredByUser || getCubeAutoAnswerEnabled()) &&
        state?.Status === 'CALLING'
      ) {
        this.cubeAwaitingAgentAnswer = false;
        this.agentAnsweredByUser = true;
        this.handleCallProgress({ type: 'CALL_ANSWERED_FALLBACK' }, 'CONNECTED');
      }
    }, 4500);
  }

  private scheduleConnectedFallback(_phoneNo?: string): void {
    return;
  }

  private broadcastCallEnd(): void {
    const state = this.callState$.getValue();
    this.incomeCallService.IncomingCallSubject.next({
      Status: 'Disconnected',
      callingType: 'cube',
      dueId: state?.dueId,
      phoneNo: state?.phoneNo,
      clientId: state?.clientId,
      timerFlag: true,
    });
    this.incomeCallService.setCallingRes({
      ...(state || {}),
      callingType: 'cube',
      Status: 'Idle',
      holdCall: false,
    });
  }

  private markCallEnded(): void {
    const currentStatus = this.callState$.getValue()?.Status;
    this.cubeLog('markCallEnded', { currentStatus, wrapUpPending: this.cubeWrapUpPending });
    if (this.cubeWrapUpPending) {
      return;
    }
    if (currentStatus === 'Disconnected' || currentStatus === 'Idle') {
      const residual =
        !!this.callState$.getValue()?.phoneNo ||
        this.cubeIframeAgentConnected ||
        this.status$.getValue() === 'on_call';
      if (residual) {
        this.notifyCallEnded('residual_idle');
      }
      return;
    }
    const endedPhone = String(this.callState$.getValue()?.phoneNo || '').replace(/\D/g, '').slice(-10);
    this.lastCallEndedAt = Date.now();
    this.lastEndedCallPhone = endedPhone;
    this.lastEndedCallHandle = this.activeCallHandle;
    this.applyConfiguredAutoAnswer();
    this.notifyCallEnded('mark_ended');
  }

  private async finishCallDispose(disposition: string): Promise<void> {
    const state = this.callState$.getValue();
    try {
      await firstValueFrom(
        this.callingService.disposeCubeCallRaphsody({
          clientId: state?.clientId || '',
          Disposition: disposition,
        })
      );
    } catch (_e) { }
    void this.bootstrapRestAuthId();
    if (!this.callEndInProgress) {
      this.markCallEnded();
    }
  }

  private forceCallCleanup(reason = 'remote'): void {
    if (this.cubeWrapUpPending || this.callLegPhase === 'wrap_up') {
      this.cubeLog('forceCallCleanup', { skip: 'wrap_up_active', reason });
      return;
    }
    if (this.callLegPhase === 'active') {
      if (!this.callLegEverConnected) {
        this.cubeLog('forceCallCleanup', { skip: 'never_connected', reason });
        return;
      }
      this.cubeLog('forceCallCleanup', { route: 'wrap_up', reason });
      if (this.shouldSkipWrapUp({ reason })) {
        this.markCallEnded();
        return;
      }
      this.beginWrapUp({ type: 'CALL_ENDED', reason });
      return;
    }
    const state = this.callState$.getValue();
    if (
      state?.Status === 'Idle' &&
      !state?.phoneNo &&
      !this.cubeIframeAgentConnected &&
      this.status$.getValue() === 'ready'
    ) {
      this.cubeLog('forceCallCleanup', { skip: 'already_idle', reason });
      return;
    }
    this.cubeLog('forceCallCleanup', { reason, status: state?.Status });
    const endedPhone = String(state?.phoneNo || '').replace(/\D/g, '').slice(-10);
    this.lastCallEndedAt = Date.now();
    this.lastEndedCallPhone = endedPhone;
    this.lastEndedCallHandle = this.activeCallHandle;
    this.applyConfiguredAutoAnswer();
    this.notifyCallEnded(reason);
  }

  private isAgentInitiatedEnd(data?: unknown): boolean {
    const reason = String((data as { reason?: string })?.reason || '');
    return reason === 'agent_hangup' || reason === 'ui_arm';
  }

  private handleRemoteCallEnd(data?: unknown): void {
    const msgType = String((data as { type?: string })?.type || '').toUpperCase();
    const reason = String((data as { reason?: string })?.reason || 'remote');
    this.cubeLog('handleRemoteCallEnd', { msgType, reason, phase: this.callLegPhase });
    if (this.isCallOnHold()) {
      const via = String((data as { via?: string })?.via || '');
      const isTerminal =
        msgType === 'CALL_ENDED' ||
        msgType === 'WRAP_UP' ||
        msgType === 'HANGUP' ||
        msgType === 'CALL_DISCONNECTED' ||
        reason === 'remote' ||
        reason === 'remote_idle' ||
        reason === 'iframe_idle_flush';
      if (!isTerminal && (via === 'callwatch' || reason === 'remote_idle')) {
        return;
      }
      this.publishCallState({ holdCall: false });
    }
    if (this.callEndInProgress) {
      return;
    }
    if (this.callLegPhase === 'wrap_up' || this.cubeWrapUpPending) {
      return;
    }
    if (!this.isAgentInitiatedEnd(data) && !this.callLegEverConnected) {
      this.cubeLog('handleRemoteCallEnd', { skip: 'never_connected', msgType, reason });
      return;
    }
    const state = this.callState$.getValue();
    const status = state?.Status;
    const spuriousIdle =
      reason === 'iframe_idle_flush' ||
      reason === 'remote_idle' ||
      reason === 'widget_iframe_idle' ||
      reason === 'widget-iframe-idle';
    if (spuriousIdle && this.callLegPhase === 'active') {
      this.cubeLog('handleRemoteCallEnd', { skip: 'spurious_idle_active_leg', reason });
      return;
    }
    const residual =
      !!state?.phoneNo ||
      !!state?.customerAnswered ||
      this.cubeAwaitingAgentAnswer ||
      this.cubeTransferRouting ||
      this.status$.getValue() === 'on_call';
    if (status === 'Idle' || status === 'Disconnected') {
      if (this.callLegPhase === 'active') {
        if (!this.shouldEnterCubeWrapUp(data)) {
          return;
        }
        if (this.shouldSkipWrapUp(data)) {
          this.markCallEnded();
        } else {
          this.beginWrapUp(data);
        }
        return;
      }
      if (residual && this.callLegEverConnected) {
        this.forceCallCleanup(String((data as { reason?: string })?.reason || 'remote_idle'));
      } else {
        this.clearRingState();
      }
      return;
    }
    const active =
      this.callLegPhase === 'active' ||
      this.isOnActiveCall() ||
      this.cubeAwaitingAgentAnswer ||
      this.status$.getValue() === 'on_call' ||
      this.cubeTransferRouting;
    if (!active) {
      this.clearRingState();
      return;
    }
    if (this.shouldSkipWrapUp(data)) {
      this.markCallEnded();
      return;
    }
    if (this.shouldEnterCubeWrapUp(data)) {
      this.beginWrapUp(data);
      return;
    }
  }

  private shouldSkipWrapUp(data?: unknown): boolean {
    const reason = String((data as { reason?: string })?.reason || '');
    const via = String((data as { via?: string })?.via || '');
    if (this.dummyTransferUiReleased) {
      return true;
    }
    if (reason === 'blind_transfer' || via === 'release_dummy') {
      return true;
    }
    return false;
  }

  private shouldEnterCubeWrapUp(data?: unknown): boolean {
    if (this.callLegPhase === 'wrap_up' || this.cubeWrapUpPending) {
      return false;
    }
    const reason = String((data as { reason?: string })?.reason || '');
    return this.canShowDisposition(reason);
  }

  private resumeCallFromPrematureWrapUp(
    status: 'CALLING' | 'CONNECTED',
    patch: Partial<CubeCallUiState> = {}
  ): void {
    this.cubeLog('resumeCallFromPrematureWrapUp', { status });
    this.cubeWrapUpPending = false;
    this.callLegPhase = 'active';
    this.callLegCancelled = false;
    this.callEndInProgress = false;
    this.callDisposeInFlight = false;
    if (status === 'CONNECTED') {
      this.callLegEverConnected = true;
      this.cubeIframeAgentConnected = true;
    } else {
      this.callLegEverConnected = false;
    }
    const current = this.callState$.getValue();
    const next: CubeCallUiState = {
      ...(current || { callingType: 'cube' }),
      ...patch,
      callingType: 'cube',
      Status: status,
      holdCall: false,
      phoneNo: patch.phoneNo || current?.phoneNo || '',
      phoneNumber: patch.phoneNumber || patch.phoneNo || current?.phoneNumber || current?.phoneNo || '',
      fullName: patch.fullName || current?.fullName || '',
      dueId: patch.dueId || current?.dueId,
      clientId: patch.clientId || current?.clientId,
    };
    this.ngZone.run(() => {
      this.callState$.next(next);
      this.setUiStatus('on_call');
      this.setIframeCallControlsActive(true);
    });
  }

  private beginWrapUp(data?: unknown): void {
    if (this.cubeWrapUpPending || this.callLegPhase === 'wrap_up') {
      this.cubeLog('beginWrapUp', { skip: 'already_active' });
      return;
    }
    const reason = String((data as { reason?: string })?.reason || '');
    const forceAgentEnd = reason === 'agent_hangup' || reason === 'ui_arm';
    if (!forceAgentEnd && !this.shouldEnterCubeWrapUp(data)) {
      this.cubeLog('beginWrapUp', {
        skip: 'not_eligible',
        reason,
        everConnected: this.callLegEverConnected,
        outboundDialSent: this.outboundDialSent,
        phase: this.callLegPhase,
      });
      return;
    }
    const snapshot =
      (data as { snapshot?: CubeCallUiState })?.snapshot &&
      typeof (data as { snapshot?: CubeCallUiState }).snapshot === 'object'
        ? (data as { snapshot: CubeCallUiState }).snapshot
        : this.callState$.getValue();
    const state = snapshot || this.callState$.getValue();
    const endedPhone = String(state?.phoneNo || '').replace(/\D/g, '').slice(-10);
    this.callLegPhase = 'wrap_up';
    this.cubeWrapUpPending = true;
    this.lastCallEndedAt = Date.now();
    this.lastEndedCallPhone = endedPhone;
    this.lastEndedCallHandle = this.activeCallHandle;
    this.cubeLog('beginWrapUp', {
      phone: state?.phoneNo || '',
      clientId: state?.clientId || '',
      dueId: state?.dueId || '',
      everConnected: this.callLegEverConnected,
      reason,
    });
    this.cancelTransferResultWait(false);
    this.cubeTransferRouting = false;
    this.transferArmSent = false;
    this.sendIframeAction('CLEAR_TRANSFER_ARM', {});
    this.clearRingState();
    this.cubeAwaitingAgentAnswer = false;
    this.setIframeCallControlsActive(false);
    this.applyConfiguredAutoAnswer();
    this.ngZone.run(() => {
      this.publishCallState({
        Status: 'Disconnected',
        holdCall: false,
        customerAnswered: false,
        phoneNo: state?.phoneNo || '',
        dueId: state?.dueId,
        clientId: state?.clientId,
        fullName: state?.fullName,
      });
      if (this.agentLoggedIn) {
        this.status$.next('ready');
      }
      this.emit(this.wrapUpStarted$, {
        ...(typeof data === 'object' && data ? data : {}),
        type: 'WRAP_UP',
        reason: reason || 'remote',
        snapshot: state,
      });
    });
  }

  private async disposeActiveCallInBackground(clientId = ''): Promise<void> {
    if (this.callDisposeInFlight) {
      return;
    }
    this.callDisposeInFlight = true;
    try {
      await firstValueFrom(
        this.callingService.disposeCubeCallRaphsody({
          clientId,
          Disposition: CUBE_DEFAULT_DISPOSITION,
        })
      );
      void this.bootstrapRestAuthId();
    } catch (_e) { }
    finally {
      this.callDisposeInFlight = false;
    }
  }

  private completeWrapUp(disposition = CUBE_DEFAULT_DISPOSITION): void {
    this.sendIframeAction('DISPOSE_CALL', { disposition });
    this.sendIframeAction('NEXT_CALL', { disposition });
  }

  private clearWrapUpWait(): void {
    if (this.wrapUpTimer) {
      clearTimeout(this.wrapUpTimer);
      this.wrapUpTimer = null;
    }
    this.wrapUpResolve = null;
  }

  private waitForWrapUp(timeoutMs = 6000): Promise<void> {
    return new Promise((resolve) => {
      if (this.wrapUpTimer) {
        clearTimeout(this.wrapUpTimer);
      }
      const done = () => {
        if (this.wrapUpTimer) {
          clearTimeout(this.wrapUpTimer);
          this.wrapUpTimer = null;
        }
        this.wrapUpResolve = null;
        resolve();
      };
      this.wrapUpResolve = done;
      this.wrapUpTimer = setTimeout(done, timeoutMs);
    });
  }

  private signalWrapUpReady(): void {
    if (!this.wrapUpResolve) {
      return;
    }
    const done = this.wrapUpResolve;
    if (this.wrapUpTimer) {
      clearTimeout(this.wrapUpTimer);
      this.wrapUpTimer = null;
    }
    this.wrapUpResolve = null;
    done();
  }

  private clearFreemeWait(): void {
    if (this.freemeTimer) {
      clearTimeout(this.freemeTimer);
      this.freemeTimer = null;
    }
    this.freemeResolve = null;
  }

  private waitForFreemeDone(timeoutMs = 4500): Promise<void> {
    return new Promise((resolve) => {
      if (this.freemeTimer) {
        clearTimeout(this.freemeTimer);
      }
      const done = () => {
        if (this.freemeTimer) {
          clearTimeout(this.freemeTimer);
          this.freemeTimer = null;
        }
        this.freemeResolve = null;
        resolve();
      };
      this.freemeResolve = done;
      this.freemeTimer = setTimeout(done, timeoutMs);
    });
  }

  private signalFreemeDone(): void {
    if (!this.freemeResolve) {
      return;
    }
    const done = this.freemeResolve;
    if (this.freemeTimer) {
      clearTimeout(this.freemeTimer);
      this.freemeTimer = null;
    }
    this.freemeResolve = null;
    done();
  }

  private shouldDisableAutoAnswerForTransfer(): boolean {
    if (this.isManualDialerCall()) {
      return false;
    }
    return isCubeAutoTransferDummySession();
  }

  private isDummyAutoTransferSession(): boolean {
    return this.shouldDisableAutoAnswerForTransfer();
  }

  private applyConfiguredAutoAnswer(): void {
    if (this.shouldDisableAutoAnswerForTransfer()) {
      this.setAutoAnswer(false);
      return;
    }
    this.setAutoAnswer(getCubeAutoAnswerEnabled());
  }

  private maybeAutoAnswerCampaign(): void {
    if (this.isManualDialerCall() || this.callSource === 'manual') {
      return;
    }
    if (this.shouldDisableAutoAnswerForTransfer()) {
      return;
    }
    if (this.cubeTransferRouting) {
      return;
    }
    if (!getCubeAutoAnswerEnabled() || !this.agentLoggedIn) {
      return;
    }
    if (this.callLegCancelled || this.callEndInProgress) {
      return;
    }
    const state = this.callState$.getValue();
    if (state?.Status === 'CONNECTED' || this.agentAnsweredByUser) {
      return;
    }
    if (this.callLegPhase === 'idle' && this.isCampaignDialerCall()) {
      this.beginCallLeg('campaign');
    }
    if (
      !this.cubeAwaitingAgentAnswer &&
      (state?.customerAnswered || this.isCampaignDialerCall())
    ) {
      this.cubeAwaitingAgentAnswer = true;
    }
    this.setAutoAnswer(true);
    this.scheduleAutoAnswerConnectedPromotion();
    if (!this.agentAnsweredByUser) {
      const answered = this.answer();
      if (!answered) {
        this.scheduleCampaignAutoAnswer(state || {});
      }
    }
  }

  private cancelTransferResultWait(resolveWith?: boolean): void {
    if (this.transferResultTimer) {
      clearTimeout(this.transferResultTimer);
      this.transferResultTimer = null;
    }
    if (this.transferResultWait) {
      const prev = this.transferResultWait;
      this.transferResultWait = null;
      if (resolveWith !== undefined) {
        prev(resolveWith);
      }
    }
  }

  private waitTransferResult(timeoutMs = 10000): Promise<boolean> {
    this.cancelTransferResultWait(false);
    return new Promise((resolve) => {
      this.transferResultTimer = setTimeout(() => {
        this.transferResultTimer = null;
        if (this.transferResultWait) {
          this.transferResultWait = null;
          resolve(false);
        }
      }, timeoutMs);
      this.transferResultWait = (ok) => {
        if (this.transferResultTimer) {
          clearTimeout(this.transferResultTimer);
          this.transferResultTimer = null;
        }
        this.transferResultWait = null;
        resolve(ok);
      };
    });
  }

  private resolveTransferResult(data: unknown): void {
    const ok = !!(data as { ok?: boolean })?.ok;
    const offering = !!(data as { offering?: boolean })?.offering;
    if (this.transferResultWait) {
      this.transferResultWait(ok);
    }
    if (ok) {
      this.cubeTransferDispatched = true;
      this.cubeTransferRouting = false;
      this.transferArmSent = false;
      this.emit(this.transferCompleted$, data);
      if (offering && this.shouldDisableAutoAnswerForTransfer() && !this.dummyTransferUiReleased) {
        this.releaseAfterBlindTransfer();
      }
    }
  }

  private async completeTransferIframe(): Promise<boolean> {
    return this.iframeCallControl('COMPLETE_TRANSFER', {});
  }

  private clearAutoAnswerPromoteTimer(): void {
    if (this.autoAnswerPromoteTimer) {
      clearTimeout(this.autoAnswerPromoteTimer);
      this.autoAnswerPromoteTimer = null;
    }
  }

  private scheduleAutoAnswerConnectedPromotion(): void {
    if (
      !getCubeAutoAnswerEnabled() ||
      this.cubeTransferRouting ||
      this.shouldDisableAutoAnswerForTransfer() ||
      this.isManualDialerCall() ||
      this.callSource === 'manual'
    ) {
      return;
    }
    this.clearAutoAnswerPromoteTimer();
    this.autoAnswerPromoteTimer = setTimeout(() => {
      this.autoAnswerPromoteTimer = null;
      if (this.callEndInProgress || this.callLegCancelled) {
        return;
      }
      if (this.isManualDialerCall() || this.callSource === 'manual') {
        return;
      }
      const state = this.callState$.getValue();
      if (state?.Status === 'CONNECTED' || this.agentAnsweredByUser) {
        return;
      }
      if (!state?.customerAnswered && !this.cubeAwaitingAgentAnswer) {
        return;
      }
      this.acceptRemoteConnection({ type: 'AUTO_ANSWER_PROMOTE', via: 'auto-answer-promote' });
    }, 800);
  }

  private connectedStatesFrom(data: unknown): string[] {
    return ((data as { states?: string[] })?.states || []).map((s) => String(s || '').toUpperCase());
  }

  private isRemoteConnectedSignal(data: unknown): boolean {
    const type = String((data as { type?: string })?.type || '').toUpperCase();
    const states = this.connectedStatesFrom(data);
    const hasLiveLeg = states.some((s) => /CONNECTED|TALKING|INCALL/.test(s));
    if (this.isManualDialerCall() || this.callSource === 'manual') {
      if (['CUSTOMER_ANSWERED', 'CUSTOMER_CONNECTED'].includes(type)) {
        return true;
      }
      if (this.agentAnsweredByUser || this.cubeIframeAgentConnected) {
        return hasLiveLeg || ['CONNECTED', 'INCALL', 'TALKING', 'CALL_CONNECTED'].includes(type);
      }
      return false;
    }
    if (['CONNECTED', 'INCALL', 'TALKING'].includes(type)) {
      return true;
    }
    if (['CUSTOMER_ANSWERED', 'CUSTOMER_CONNECTED', 'AGENT_OFFERING'].includes(type)) {
      return hasLiveLeg;
    }
    if (type === 'AUTO_ANSWER_PROMOTE') {
      return getCubeAutoAnswerEnabled() && !this.shouldDisableAutoAnswerForTransfer();
    }
    if (type === 'CALL_CONNECTED' || type === 'CALL_ANSWERED' || type === 'ANSWER') {
      const via = String((data as { via?: string })?.via || '');
      if (this.shouldDisableAutoAnswerForTransfer()) {
        return via === 'callwatch' && hasLiveLeg;
      }
      if (via === 'callwatch') {
        return hasLiveLeg;
      }
      return hasLiveLeg || getCubeAutoAnswerEnabled();
    }
    return hasLiveLeg;
  }

  private acceptIframeAgentConnection(data: unknown): void {
    if (this.callLegCancelled) {
      this.cubeLog('acceptIframeAgentConnection', { skip: 'agent_hangup' });
      return;
    }
    if (this.cubeWrapUpPending || this.callLegPhase === 'wrap_up') {
      this.resumeCallFromPrematureWrapUp('CONNECTED', this.extractCallMeta(data));
    }
    if (this.shouldDisableAutoAnswerForTransfer() && !this.dummyTransferUiReleased) {
      this.cubeLog('acceptIframeAgentConnection', { soft: 'dummy_auto_transfer_ui' });
      if (this.callLegPhase === 'idle') {
        this.beginCallLeg('campaign');
      }
      this.callEndInProgress = false;
      this.callLegCancelled = false;
      this.cubeAwaitingAgentAnswer = false;
      this.cubeIframeAgentConnected = true;
      this.agentAnsweredByUser = true;
      this.markLegConnected();
      this.clearConnectTimer();
      const meta = this.extractCallMeta(data);
      const cur = this.callState$.getValue();
      this.publishCallState({
        Status: 'CONNECTED',
        customerAnswered: true,
        callDirection: 'campaign',
        holdCall: !!cur?.holdCall,
        ...meta,
      });
      this.setUiStatus('on_call');
      this.setIframeCallControlsActive(true);
      this.setTransferRouting(true);
      this.emit(this.campaignCustomerAnswered$, data);
      return;
    }
    if (
      (this.isManualDialerCall() || this.callSource === 'manual') &&
      !this.agentAnsweredByUser &&
      !this.cubeIframeAgentConnected &&
      !this.isRemoteConnectedSignal(data)
    ) {
      this.cubeLog('acceptIframeAgentConnection', { skip: 'manual_outbound_early_media' });
      this.handleCallProgress(data, 'CALLING');
      return;
    }
    const status = this.callState$.getValue()?.Status;
    this.cubeLog('acceptIframeAgentConnection', {
      status,
      transferRouting: this.cubeTransferRouting,
      transferInFlight: this.cubeTransferInFlight,
      transferArmSent: this.transferArmSent,
    });
    if (
      this.callEndInProgress &&
      status !== 'CALLING' &&
      status !== 'CONNECTED' &&
      !this.cubeAwaitingAgentAnswer
    ) {
      return;
    }
    this.callEndInProgress = false;
    this.callLegCancelled = false;
    const keepTransferArm = this.cubeTransferRouting || this.cubeTransferInFlight || this.transferArmSent;
    if (!keepTransferArm) {
      this.cubeTransferRouting = false;
      this.transferArmSent = false;
      this.sendIframeAction('CLEAR_TRANSFER_ARM', {});
    }
    this.markLegConnected();
    this.cubeAwaitingAgentAnswer = false;
    this.cubeIframeAgentConnected = true;
    this.agentAnsweredByUser = true;
    this.clearConnectTimer();
    this.handleCallProgress(data, 'CONNECTED');
    const cur = this.callState$.getValue();
    const meta = this.extractCallMeta(data);
    this.publishCallState({
      Status: 'CONNECTED',
      customerAnswered: true,
      holdCall: !!cur?.holdCall,
      ...meta,
    });
    this.setUiStatus('on_call');
    this.setIframeCallControlsActive(true);
    this.ensureOpenCaseFromConnected(meta);
  }

  private refreshIframeCallState(data: unknown): void {
    if (this.callEndInProgress || this.callLegCancelled) {
      return;
    }
    const cur = this.callState$.getValue();
    const meta = this.extractCallMeta(data);
    const onHold = !!(data as { hold?: boolean })?.hold;
    if (onHold) {
      this.publishCallState({ holdCall: true });
    }
    if (cur?.Status !== 'CONNECTED') {
      this.cubeIframeAgentConnected = true;
      this.cubeAwaitingAgentAnswer = false;
      this.agentAnsweredByUser = true;
      this.markLegConnected();
      this.publishCallState({
        Status: 'CONNECTED',
        customerAnswered: true,
        holdCall: onHold || !!cur?.holdCall,
        ...meta,
      });
    } else if (meta.phoneNo || meta.dueId) {
      this.publishCallState(meta);
    }
    this.setUiStatus('on_call');
    this.setIframeCallControlsActive(true);
  }

  private acceptRemoteConnection(data: unknown): void {
    this.acceptIframeAgentConnection(data);
  }

  private async endActiveCall(
    disposition = CUBE_DEFAULT_DISPOSITION,
    force = false,
    opts: { skipHangup?: boolean } = {}
  ): Promise<void> {
    if (this.callEndInProgress || this.callDisposeInFlight) {
      return;
    }
    if (!force && !this.isOnActiveCall() && !this.cubeAwaitingAgentAnswer && this.status$.getValue() !== 'on_call') {
      return;
    }
    this.beginCallTeardown();
    this.callDisposeInFlight = true;
    try {
      const wrapUpWait = this.waitForWrapUp();
      if (opts.skipHangup) {
        this.signalWrapUpReady();
      } else {
        this.tearDownCallLeg();
      }
      await wrapUpWait;
      const freemeWait = this.waitForFreemeDone();
      this.completeWrapUp(disposition);
      await freemeWait;
      await this.finishCallDispose(disposition);
    } finally {
      this.clearWrapUpWait();
      this.clearFreemeWait();
      this.callDisposeInFlight = false;
    }
  }

  private extractAuthId(data: unknown): string | undefined {
    const root =
      (data as { payload?: Record<string, unknown> })?.payload ||
      (data as Record<string, unknown>) ||
      {};
    const raw = root['AuthId'] || root['authId'] || root['sessionId'] || root['SessionId'];
    return raw ? String(raw) : undefined;
  }

  private syncAuthIdToServer(authId: string, data?: unknown): void {
    void firstValueFrom(
      this.callingService.syncCubeSession({
        authId,
        session: (data as Record<string, unknown>) || {},
      })
    ).catch((e) => {
      console.warn('[CubeConnector] AuthId sync failed', e);
    });
  }

  private setIframeCallControlsActive(active: boolean): void {
    this.ngZone.run(() => this.iframeCallControlsActive$.next(active));
  }

  private isOnActiveCall(): boolean {
    const status = this.callState$.getValue()?.Status;
    return status === 'CALLING' || status === 'CONNECTED';
  }

  private isOnConnectedCall(): boolean {
    return (
      this.callState$.getValue()?.Status === 'CONNECTED' ||
      this.cubeIframeAgentConnected
    );
  }

  isAgentLegConnected(): boolean {
    return this.isOnConnectedCall();
  }

  private async hasRestAuthId(): Promise<boolean> {
    try {
      const res = await firstValueFrom(this.callingService.getCubeSessionStatus()) as { hasAuthId?: boolean };
      return !!res?.hasAuthId;
    } catch (_e) {
      return false;
    }
  }

  private isCampaignCustomerDialing(): boolean {
    const state = this.callState$.getValue();
    return (
      state?.callDirection === 'campaign' &&
      state?.Status === 'CALLING' &&
      !state?.customerAnswered
    );
  }

  private resolveInboundCallSource(data: unknown): 'inbound' | 'campaign' {
    if (this.callSource === 'manual') {
      return 'inbound';
    }
    if (this.pendingUploadLeads.length > 0) {
      return 'campaign';
    }
    const profile = getCubeAutoTransferProfile();
    if (
      profile.enabled &&
      isCubeAutoTransferSourceCampaign(profile.loginCampaign)
    ) {
      return 'campaign';
    }
    if (this.callState$.getValue()?.customerAnswered) {
      return 'campaign';
    }
    const phone = this.cubeDialerFlow.getPhoneNumber(data);
    if (phone) {
      const normalized = phone.replace(/\D/g, '').slice(-10);
      if (
        this.pendingUploadLeads.some((lead) => {
          const leadPhone = String(lead?.phoneNo || '')
            .replace(/\D/g, '')
            .slice(-10);
          return leadPhone.length >= 10 && leadPhone === normalized;
        })
      ) {
        return 'campaign';
      }
    }
    return 'inbound';
  }

  private shouldSuppressCampaignAgentUi(): boolean {
    if (
      this.cubeAwaitingAgentAnswer ||
      this.agentAnsweredByUser ||
      this.cubeIframeAgentConnected ||
      this.getCurrentStatus() === 'on_call'
    ) {
      return false;
    }
    return (
      this.callSource === 'campaign' &&
      !this.callState$.getValue()?.customerAnswered
    );
  }

  private isAgentLegRinging(data: unknown): boolean {
    const type = String((data as CubeWebphoneMessage)?.type || '').toUpperCase();
    if (['OUTBOUND_CALL', 'CALL_STARTED', 'OUTBOUND_MANUAL_CALL'].includes(type)) {
      return true;
    }
    const enriched = this.cubeDialerFlow.enrichCallDisplayData(data);
    const status = String(enriched?.Status || enriched?.['status'] || '').toUpperCase();
    return ['RING', 'RINGING', 'DIAL', 'DIALING', 'CALLING', 'PROGRESS'].includes(status);
  }

  private canPromoteToConnected(): boolean {
    if (this.cubeIframeAgentConnected || this.agentAnsweredByUser) {
      return true;
    }
    if (this.isManualDialerCall() || this.callSource === 'manual') {
      return false;
    }
    if (this.cubeTransferRouting || this.isDummyAutoTransferSession()) {
      return false;
    }
    return (
      getCubeAutoAnswerEnabled() &&
      (this.callSource === 'campaign' || this.pendingUploadLeads.length > 0) &&
      !!this.callState$.getValue()?.customerAnswered
    );
  }

  private shouldBlockCampaignConnected(_data: unknown): boolean {
    if (this.canPromoteToConnected()) {
      return false;
    }
    if (this.cubeAwaitingAgentAnswer) {
      return true;
    }
    return this.callSource === 'inbound' || this.callSource === 'campaign';
  }

  private primeAgentAnswerRing(data: unknown): void {
    if (this.shouldIgnoreStaleCallEvent(data)) {
      return;
    }
    if (this.callState$.getValue()?.Status === 'CONNECTED' || this.agentAnsweredByUser) {
      return;
    }
    this.callLegCancelled = false;
    this.cubeAwaitingAgentAnswer = true;
    const dummyTransfer = this.isDummyAutoTransferSession();
    if (dummyTransfer) {
      this.setAutoAnswer(false);
      this.setTransferRouting(true);
    } else if (getCubeAutoAnswerEnabled()) {
      this.setAutoAnswer(true);
    } else {
      this.setAutoAnswer(false);
    }
    const extracted = this.extractCallMeta(data);
    const meta = {
      ...this.resolvePendingLeadMeta(extracted.phoneNo),
      ...this.pendingCampaignMeta,
      ...extracted,
    };
    if (!meta.phoneNo) {
      Object.assign(meta, this.resolvePendingLeadMeta());
    }
    this.pendingCampaignMeta = {};
    const isCampaign =
      this.callSource === 'campaign' ||
      (this.pendingUploadLeads.length > 0 && !this.isManualDialerCall()) ||
      dummyTransfer;
    if (isCampaign && this.callSource !== 'campaign' && !this.isManualDialerCall()) {
      this.callSource = 'campaign';
    }
    this.publishCallState({
      Status: 'CALLING',
      customerAnswered: true,
      callDirection: isCampaign ? 'campaign' : this.callSource || 'inbound',
      ...meta,
    });
    this.setUiStatus('on_call');
    this.setIframeCallControlsActive(true);
    const state = this.callState$.getValue();
    if (state) {
      this.incomeCallService.setCallingRes({ ...state, callingType: 'cube', Status: 'CALLING' });
    }
    if (dummyTransfer) {
      this.emit(this.campaignCustomerAnswered$, data);
    } else if (isCampaign) {
      if (!this.cubeTransferRouting && getCubeAutoAnswerEnabled()) {
        this.maybeAutoAnswerCampaign();
      }
    }
  }
  private shouldTreatAsCustomerAnswered(
    data: unknown,
    requestedStatus: 'CALLING' | 'CONNECTED' = 'CALLING'
  ): boolean {
    if (!this.canPromoteToConnected()) {
      return false;
    }
    if (this.cubeAwaitingAgentAnswer && requestedStatus !== 'CONNECTED') {
      return false;
    }
    const type = String((data as CubeWebphoneMessage)?.type || '').toUpperCase();
    if (this.agentAnsweredByUser) {
      if (requestedStatus === 'CONNECTED') {
        return true;
      }
      if (['RING', 'RINGING', 'DIAL', 'DIALING', 'CALLING', 'PROGRESS', 'INBOUND_CALL'].includes(type)) {
        return false;
      }
      return true;
    }
    if (this.callSource !== 'campaign') {
      return true;
    }
    if (['CUSTOMER_ANSWERED', 'CUSTOMER_CONNECTED'].includes(type)) {
      return true;
    }
    if (
      ['CALL_CONNECTED', 'CALL_ANSWERED', 'ANSWER', 'CONNECTED', 'INCALL', 'TALKING'].includes(type)
    ) {
      return !!this.callState$.getValue()?.customerAnswered;
    }
    const enriched = this.cubeDialerFlow.enrichCallDisplayData(data);
    const status = String(enriched?.Status || enriched?.['status'] || '').toUpperCase();
    if (['RING', 'RINGING', 'DIAL', 'DIALING', 'CALLING', 'PROGRESS'].includes(status)) {
      return false;
    }
    return !!this.callState$.getValue()?.customerAnswered;
  }

  private handleCampaignCustomerAnswered(data: unknown): void {
    if (this.isManualDialerCall()) {
      return;
    }
    if (this.shouldIgnoreStaleCallEvent(data)) {
      return;
    }
    if (this.agentAnsweredByUser || this.callState$.getValue()?.Status === 'CONNECTED') {
      return;
    }
    if (this.isRemoteConnectedSignal(data)) {
      this.acceptIframeAgentConnection(data);
      return;
    }
    const disableAutoAnswer = this.shouldDisableAutoAnswerForTransfer();
    const current = this.callState$.getValue();
    if (current?.customerAnswered && current?.Status === 'CALLING') {
      if (this.callLegPhase === 'idle') {
        this.beginCallLeg('campaign');
      }
      if (!this.agentAnsweredByUser && !this.cubeAwaitingAgentAnswer) {
        this.cubeAwaitingAgentAnswer = true;
      }
      if (disableAutoAnswer && !this.cubeTransferRouting) {
        this.setTransferRouting(true);
      }
      this.emit(this.campaignCustomerAnswered$, data);
      if (!disableAutoAnswer && !this.cubeTransferRouting && getCubeAutoAnswerEnabled()) {
        this.maybeAutoAnswerCampaign();
      }
      return;
    }
    if (this.shouldIgnoreStaleCallEvent(data)) {
      return;
    }
    if (!this.alignCallLegForProgress(data)) {
      return;
    }
    const handle = this.extractCallHandle(data);
    if (handle) {
      this.activeCallHandle = handle;
    }
    this.callLegCancelled = false;
    this.cubeAwaitingAgentAnswer = true;
    this.agentAnsweredByUser = false;
    if (this.callLegPhase === 'idle') {
      this.beginCallLeg('campaign');
    }
    if (disableAutoAnswer) {
      this.setTransferRouting(true);
      this.setAutoAnswer(false);
    } else if (!this.cubeTransferRouting && getCubeAutoAnswerEnabled()) {
      this.setAutoAnswer(true);
    } else {
      this.setAutoAnswer(false);
    }
    if (this.callSource !== 'campaign' && !this.isManualDialerCall()) {
      if (this.pendingUploadLeads.length > 0) {
        this.setCallSourceCampaignIfAllowed();
      } else {
        return;
      }
    }
    const extracted = this.extractCallMeta(data);
    const meta = {
      ...this.resolvePendingLeadMeta(extracted.phoneNo),
      ...this.pendingCampaignMeta,
      ...extracted,
    };
    if (!meta.phoneNo) {
      Object.assign(meta, this.resolvePendingLeadMeta());
    }
    this.pendingCampaignMeta = {};
    this.publishCallState({
      Status: 'CALLING',
      customerAnswered: true,
      callDirection: 'campaign',
      ...meta,
    });
    this.setUiStatus('on_call');
    this.setIframeCallControlsActive(true);
    this.emit(this.inboundCall$, data);
    const state = this.callState$.getValue();
    if (state) {
      this.incomeCallService.setCallingRes({ ...state, callingType: 'cube', Status: 'CALLING' });
    }
    this.emit(this.campaignCustomerAnswered$, data);
    if (!disableAutoAnswer && !this.cubeTransferRouting) {
      this.maybeAutoAnswerCampaign();
    }
  }

  private scheduleCampaignAutoAnswer(meta: Partial<CubeCallUiState>): void {
    if (this.cubeTransferRouting) {
      return;
    }
    let tries = 0;
    const tick = () => {
      tries += 1;
      if (this.callLegCancelled || this.callEndInProgress) {
        return;
      }
      if (this.callState$.getValue()?.Status === 'CONNECTED') {
        return;
      }
      this.sendIframeAction('ANSWER');
      this.sendIframeAction('ACCEPT_CALL');
      if (tries < 10) {
        setTimeout(tick, 700);
      }
    };
    setTimeout(tick, 400);
  }

  private buildCallStatePatch(
    resolvedStatus: 'CALLING' | 'CONNECTED',
    meta: Partial<CubeCallUiState>
  ): Partial<CubeCallUiState> {
    const current = this.callState$.getValue();
    const patch: Partial<CubeCallUiState> = {
      Status: resolvedStatus,
    callDirection:
      this.callSource === 'manual'
        ? 'manual'
        : this.callSource === 'campaign'
          ? 'campaign'
          : current?.callDirection || undefined,
    };
    if (resolvedStatus === 'CONNECTED') {
      patch.customerAnswered = true;
      Object.assign(patch, meta);
      return patch;
    }
    if (this.callSource === 'campaign' || current?.callDirection === 'campaign') {
      patch.customerAnswered = !!current?.customerAnswered || this.cubeAwaitingAgentAnswer;
      Object.assign(patch, meta);
      return patch;
    }
    Object.assign(patch, meta);
    return patch;
  }

  private handleCallProgress(data: unknown, status: 'CALLING' | 'CONNECTED'): void {
    if (this.callLegCancelled) {
      return;
    }
    if (this.cubeWrapUpPending || this.callLegPhase === 'wrap_up') {
      this.resumeCallFromPrematureWrapUp(status, this.extractCallMeta(data));
      return;
    }
    if (!this.alignCallLegForProgress(data)) {
      return;
    }
    const handle = this.extractCallHandle(data);
    if (handle) {
      this.activeCallHandle = handle;
    }
    if (this.callEndInProgress || this.callLegCancelled) {
      return;
    }
    const currentStatus = this.callState$.getValue()?.Status;
    if (status === 'CALLING' && currentStatus === 'CONNECTED') {
      return;
    }
    if (this.agentAnsweredByUser && status === 'CALLING' && currentStatus === 'CONNECTED') {
      return;
    }
    if (!this.callSource && this.pendingUploadLeads.length > 0) {
      this.callSource = 'campaign';
    }
    if (
      status === 'CONNECTED' &&
      (this.callSource === 'campaign' || this.pendingUploadLeads.length > 0) &&
      !this.callState$.getValue()?.customerAnswered
    ) {
      const seeded = {
        ...this.pendingCampaignMeta,
        ...this.resolvePendingLeadMeta(this.callState$.getValue()?.phoneNo),
        ...this.extractCallMeta(data),
      };
      if (!seeded.phoneNo) {
        Object.assign(seeded, this.resolvePendingLeadMeta());
      }
      this.publishCallState({
        customerAnswered: true,
        callDirection: 'campaign',
        Status: 'CALLING',
        ...seeded,
      });
    }
    if (
      status === 'CONNECTED' &&
      getCubeAutoAnswerEnabled() &&
      (this.callSource === 'campaign' || this.pendingUploadLeads.length > 0)
    ) {
      this.maybeAutoAnswerCampaign();
    }
    if (status === 'CONNECTED' && !this.canPromoteToConnected()) {
      if (this.isRemoteConnectedSignal(data)) {
        this.acceptIframeAgentConnection(data);
        return;
      }
      if (!this.cubeAwaitingAgentAnswer) {
        this.cubeAwaitingAgentAnswer = true;
        if (!getCubeAutoAnswerEnabled()) {
          this.setAutoAnswer(false);
        }
      }
      status = 'CALLING';
    }
    if (status === 'CONNECTED' && this.cubeAwaitingAgentAnswer && !this.callState$.getValue()?.customerAnswered && !this.agentAnsweredByUser && this.getCurrentStatus() !== 'on_call') {
      status = 'CALLING';
    }
    if (this.shouldSuppressCampaignAgentUi() && status === 'CALLING') {
      this.pendingCampaignMeta = {
        ...this.pendingCampaignMeta,
        ...this.extractCallMeta(data),
      };
      return;
    }
    const resolvedStatus =
      status === 'CONNECTED' && !this.shouldTreatAsCustomerAnswered(data, status) ? 'CALLING' : status;
    const meta = {
      ...this.pendingCampaignMeta,
      ...this.extractCallMeta(data),
    };
    if (!meta.phoneNo) {
      Object.assign(meta, this.resolvePendingLeadMeta());
    }
    const phoneNo = meta.phoneNo;
    if (resolvedStatus === 'CONNECTED' && !this.canPromoteToConnected()) {
      this.publishCallState(this.buildCallStatePatch('CALLING', meta));
      this.emit(this.inboundCall$, data);
      const ringing = this.callState$.getValue();
      if (ringing) {
        this.incomeCallService.setCallingRes({ ...ringing, callingType: 'cube', Status: 'CALLING' });
      }
      return;
    }
    if (resolvedStatus === 'CONNECTED') {
      this.markLegConnected();
      this.cubeAwaitingAgentAnswer = false;
      this.setUiStatus('on_call');
      this.setIframeCallControlsActive(true);
    } else if (this.agentAnsweredByUser || currentStatus === 'CONNECTED' || this.cubeAwaitingAgentAnswer) {
      this.setUiStatus('on_call');
      this.setIframeCallControlsActive(true);
    } else if (this.agentLoggedIn) {
      this.setUiStatus('ready');
      this.setIframeCallControlsActive(true);
    }
    if (resolvedStatus === 'CALLING' && this.callLegPhase === 'idle') {
      this.beginCallLeg(this.callSource || 'inbound');
    }
    this.publishCallState(this.buildCallStatePatch(resolvedStatus, meta));
    if (resolvedStatus === 'CALLING') {
      this.emit(this.inboundCall$, data);
      const ringing = this.callState$.getValue();
      if (ringing) {
        this.incomeCallService.setCallingRes({ ...ringing, callingType: 'cube', Status: 'CALLING' });
      }
      if (this.callSource === 'manual') {
        this.scheduleConnectedFallback(phoneNo);
      }
    } else {
      this.clearConnectTimer();
      this.emit(this.callAnswered$, data);
      const state = this.callState$.getValue();
      const leadMeta = this.resolvePendingLeadMeta(phoneNo || state?.phoneNo);
      const openState = {
        ...(state || {}),
        ...leadMeta,
        ...meta,
        callingType: 'cube' as const,
        Status: 'CONNECTED' as const,
        customerAnswered: true,
      };
      this.incomeCallService.setCallingRes({ ...openState, callingType: 'cube', Status: 'CONNECTED' });
      if (openState.phoneNo || openState.dueId || openState.clientId) {
        void this.cubeDialerFlow.openCaseFromCall(openState, 'CONNECTED').then((caseData) => {
          this.applyLookedUpCallerDetails(caseData);
        });
      }
    }
  }

  private installUploadDialerWatch(): void {
    if (this.uploadDialerWatchInstalled) {
      return;
    }
    this.uploadDialerWatchInstalled = true;
    this.incomeCallService.intializeIncomeSocket();
    this.incomeCallService.onCubeDialerLeadsUploaded().subscribe((data) => {
      const uid = this.incomeCallService.uniqueUserId;
      if (uid && data?.userUniqueId && String(data.userUniqueId) !== String(uid)) {
        return;
      }
      void this.handleCampaignDialNudge(data?.campaignId, data?.leads);
    });
    this.installManualDialWatch();
  }

  private installManualDialWatch(): void {
    if (this.manualDialWatchInstalled) {
      return;
    }
    this.manualDialWatchInstalled = true;
    this.incomeCallService.onCubeManualDialRequest().subscribe((data) => {
      const uid = this.incomeCallService.uniqueUserId;
      if (uid && data?.userUniqueId && String(data.userUniqueId) !== String(uid)) {
        return;
      }
      void this.handleServerManualDial(data);
    });
  }

  private async handleServerManualDial(data?: {
    PhoneNo?: string;
    Name?: string;
    ClientId?: string;
  }): Promise<void> {
    const phone = String(data?.PhoneNo || '').replace(/\D/g, '').slice(-10);
    if (phone.length < 10 || !this.agentLoggedIn || this.isOnActiveCall()) {
      return;
    }
    const clientId = String(data?.ClientId || '').trim();
    const dueId = clientId.includes(':') ? clientId.split(':').pop() : '';
    console.log('[CubeConnector] handleServerManualDial', { phone, clientId });
    if (
      this.getCurrentStatus() === 'on_break' ||
      !!this.callState$.getValue()?.pauseCall
    ) {
      await this.breakEnd();
    }
    this.callSource = 'manual';
    this.makeCall(phone, {
      clientId,
      phoneNo: phone,
      fullName: data?.Name || '',
      ...(dueId ? { dueId: Number(dueId) || dueId } : {}),
    });
  }

  private dialLeadViaWebphone(lead?: {
    phoneNo?: string;
    fieldString2?: string;
    clientId?: string;
    fullName?: string;
    name?: string;
  }): void {
    const phone = String(lead?.phoneNo || '').replace(/\D/g, '').slice(-10);
    if (phone.length < 10 || !this.agentLoggedIn || this.isOnActiveCall()) {
      return;
    }
    const clientId = String(lead?.fieldString2 || lead?.clientId || '').trim();
    const dueId = clientId.includes(':') ? clientId.split(':').pop() : '';
    const fullName = String(lead?.fullName || lead?.name || '').trim();
    console.log('[CubeConnector] dialLeadViaWebphone', { phone, clientId });
    this.callSource = 'campaign';
    this.makeCall(phone, {
      clientId,
      ...(dueId ? { dueId: Number(dueId) || dueId } : {}),
      ...(fullName ? { fullName } : {}),
    });
  }

  private async handleCampaignDialNudge(
    campaignId?: string,
    leads?: Array<{ phoneNo?: string; fieldString2?: string; clientId?: string }>
  ): Promise<void> {
    if (leads?.length) {
      this.pendingUploadLeads = leads;
    }
    const dialLeads = leads?.length ? leads : this.pendingUploadLeads;
    if (!dialLeads.length) {
      return;
    }
    const now = Date.now();
    if (this.campaignDialNudgeInFlight || now - this.lastCampaignDialNudgeAt < 20000) {
      return;
    }
    if (!this.agentLoggedIn || this.isOnActiveCall()) {
      return;
    }
    this.campaignDialNudgeInFlight = true;
    this.lastCampaignDialNudgeAt = now;
    this.campaignDialNudgeInFlight = false;
  }

  nudgeCampaignDialAfterUpload(
    campaignId?: string,
    leads?: Array<{ phoneNo?: string; fieldString2?: string; clientId?: string }>
  ): void {
    void this.handleCampaignDialNudge(campaignId, leads);
  }

  private installMessageBridge(): void {
    if (this.messageBridgeInstalled) {
      return;
    }
    window.addEventListener('message', this.boundMessageBridge);
    this.messageBridgeInstalled = true;
  }

  private removeMessageBridge(): void {
    if (!this.messageBridgeInstalled) {
      return;
    }
    window.removeEventListener('message', this.boundMessageBridge);
    this.messageBridgeInstalled = false;
  }

  private isDuplicateSessionError(data: unknown): boolean {
    const text = typeof data === 'string' ? data : JSON.stringify(data || {});
    const lower = text.toLowerCase();
    return (
      lower.includes('already logged') ||
      lower.includes('another terminal') ||
      lower.includes('some other terminal') ||
      lower.includes('idexist') ||
      lower.includes('maximum logged')
    );
  }

  private extractLoginErrorMessage(data: unknown): string {
    if (typeof data === 'string' && data.trim()) {
      return data.trim();
    }
    const d = (data || {}) as { reason?: string; message?: string; error?: string; msg?: string };
    return String(d.reason || d.message || d.error || d.msg || 'Login failed').trim() || 'Login failed';
  }

  getLastLoginError(): string {
    return this.lastLoginError;
  }

  private isTrustedCubeOrigin(origin: string): boolean {
    const expected = getCubeConnectorChildOrigin();
    if (!origin || !expected) {
      return false;
    }
    if (origin === expected) {
      return true;
    }
    try {
      return new URL(origin).origin === new URL(expected).origin;
    } catch (_e) {
      return false;
    }
  }

  private handleWebphoneMessage(event: MessageEvent): void {
    if (!this.isTrustedCubeOrigin(event.origin)) {
      return;
    }
    const data = event.data as CubeWebphoneMessage;
    if (!data || typeof data !== 'object') {
      return;
    }
    if (data.source !== 'WEBPHONE' && data.source !== 'child') {
      return;
    }
    const msgType = String(data.type || '').toUpperCase();
    if (
      (this.callEndInProgress || this.callLegCancelled) &&
      [
        'CALL_ANSWERED',
        'ANSWER',
        'CALL_CONNECTED',
        'CONNECTED',
        'INCALL',
        'TALKING',
        'INBOUND_CALL',
        'CUSTOMER_ANSWERED',
        'CUSTOMER_CONNECTED',
        'AGENT_OFFERING',
      ].includes(msgType)
    ) {
      const revive = [
        'CALL_ANSWERED',
        'ANSWER',
        'CALL_CONNECTED',
        'CONNECTED',
        'INCALL',
        'TALKING',
      ].includes(msgType);
      const active =
        this.cubeAwaitingAgentAnswer ||
        this.callState$.getValue()?.Status === 'CALLING' ||
        this.callState$.getValue()?.Status === 'CONNECTED';
      if (revive && active) {
        this.callEndInProgress = false;
        this.callLegCancelled = false;
      } else {
        if (msgType !== 'INBOUND_CALL') {
          this.tearDownCallLeg();
        }
        return;
      }
    }
    if (
      ['CUSTOMER_ANSWERED', 'CUSTOMER_CONNECTED', 'AGENT_OFFERING', 'INBOUND_CALL'].includes(msgType) &&
      this.shouldIgnoreStaleCallEvent(data)
    ) {
      return;
    }
    switch (msgType) {
      case 'SOFTPHONE_READY':
        this.markReady(data);
        break;
      case 'LOGGED_IN':
        this.markLoggedIn(data);
        break;
      case 'LOGIN_FAILED':
        this.markLoginFailed(data);
        break;
      case 'LOGGED_OUT':
        this.markLoggedOut(data);
        break;
      case 'TRANSFER_AGENTS_LIST':
        this.resolveTransferAgents(data);
        break;
      case 'TRANSFER_SENT':
        this.cubeTransferDispatched = true;
        this.resolveTransferResult({ ...(data as object), ok: true });
        break;
      case 'TRANSFER_RESULT':
        if ((data as { ok?: boolean })?.ok) {
          this.cubeTransferDispatched = true;
        }
        this.resolveTransferResult(data);
        break;
      case 'CALL_ON_HOLD':
        this.publishCallState({ holdCall: true });
        break;
      case 'CALL_UNHOLD':
        this.publishCallState({ holdCall: false });
        break;
      case 'CALL_STATE_SYNC': {
        const states = this.connectedStatesFrom(data);
        const explicitLive = (data as { live?: boolean })?.live;
        const inferredLive = states.some((s) => /CONNECTED|TALKING|INCALL|SPEAKING|HOLD/.test(s));
        const live =
          explicitLive === false ? false : explicitLive === true ? true : inferredLive;
        const onHold = !!(data as { hold?: boolean })?.hold || states.some((s) => /HOLD/.test(s));
        const idleFlush = String((data as { reason?: string })?.reason || '') === 'idle_flush';
        this.cubeLog('CALL_STATE_SYNC', {
          live,
          explicitLive,
          onHold,
          states,
          idleFlush,
          transferRouting: this.cubeTransferRouting,
        });
        if (onHold) {
          this.publishCallState({ holdCall: true });
        }
        if (live) {
          this.cubeIframeIdleTicks = 0;
          if (this.callLegCancelled) {
            break;
          }
          if (this.cubeWrapUpPending || this.callLegPhase === 'wrap_up') {
            this.resumeCallFromPrematureWrapUp(
              this.callLegEverConnected || this.agentAnsweredByUser ? 'CONNECTED' : 'CALLING',
              this.extractCallMeta(data)
            );
          }
          const blockDummyConnect =
            this.shouldDisableAutoAnswerForTransfer() &&
            this.cubeAwaitingAgentAnswer &&
            !this.cubeTransferDispatched &&
            !this.cubeIframeAgentConnected;
          if (blockDummyConnect) {
            const meta = this.extractCallMeta(data);
            this.publishCallState({
              Status: 'CALLING',
              customerAnswered: true,
              callDirection: 'campaign',
              ...meta,
            });
            this.setUiStatus('on_call');
            this.emit(this.campaignCustomerAnswered$, data);
            break;
          }
          const curStatus = this.callState$.getValue()?.Status;
          if (
            this.callSource === 'manual' &&
            curStatus === 'CALLING' &&
            !this.cubeIframeAgentConnected &&
            !this.agentAnsweredByUser &&
            !this.callLegEverConnected
          ) {
            const talking = states.some((s) => /SPEAKING|TALKING/.test(s));
            const inCall = states.some((s) => /INCALL|CONNECTED/.test(s));
            if (talking || (inCall && this.outboundDialSent && Date.now() - this.callLegStartedAt > 2500)) {
              this.acceptIframeAgentConnection({
                ...(data as object),
                type: 'CUSTOMER_ANSWERED',
                via: 'manual_live_sync',
              });
              break;
            }
            this.publishCallState(this.extractCallMeta(data));
            break;
          }
          if (!this.cubeIframeAgentConnected) {
            this.acceptIframeAgentConnection(data);
          } else {
            this.refreshIframeCallState(data);
          }
        } else {
          if (!onHold) {
            this.publishCallState({ holdCall: false });
          }
          if (this.shouldIgnoreIframeIdleFlush()) {
            this.cubeIframeIdleTicks = 0;
            break;
          }
          if (this.lastCallEndedAt && Date.now() - this.lastCallEndedAt < 2000) {
            this.cubeIframeIdleTicks = 0;
            break;
          }
          this.cubeIframeIdleTicks += 1;
          const needsConfirm =
            this.callState$.getValue()?.Status === 'CONNECTED' ||
            this.callState$.getValue()?.Status === 'CALLING' ||
            this.cubeIframeAgentConnected ||
            this.agentAnsweredByUser ||
            this.cubeAwaitingAgentAnswer ||
            this.callLegPhase === 'active';
          const ticksRequired = needsConfirm ? 3 : 1;
          if (explicitLive === false || idleFlush || this.cubeIframeIdleTicks >= ticksRequired) {
            if (!this.cubeTransferRouting && !this.cubeTransferInFlight) {
              this.flushIframeCallState(data, false);
            }
          }
        }
        break;
      }
      case 'UI_INTERACTION': {
        const icon = String((data as { icon?: string })?.icon || '').toLowerCase();
        if (
          icon === 'call' ||
          icon === 'call-outline' ||
          /ringing/i.test(String((data as { className?: string })?.className || ''))
        ) {
          if (this.callSource === 'manual' || this.isManualDialerCall()) {
            break;
          }
          if (this.isDummyAutoTransferSession() && !this.cubeTransferDispatched) {
            break;
          }
          if (
            this.cubeAwaitingAgentAnswer ||
            (this.callState$.getValue()?.Status === 'CALLING' && !this.cubeIframeAgentConnected)
          ) {
            setTimeout(() => {
              if (
                this.callSource === 'manual' ||
                this.isManualDialerCall() ||
                this.cubeIframeAgentConnected ||
                this.isOnConnectedCall()
              ) {
                return;
              }
              this.acceptIframeAgentConnection({ ...(data as object), type: 'CALL_ANSWERED', via: 'ui_interaction' });
            }, 150);
          }
        }
        break;
      }
      case 'ON_BREAK':
      case 'BREAK_STARTED': {
        const incomingBreak = this.extractIncomingBreakLabel(data);
        if (this.shouldAutoClearLoginBreak(incomingBreak)) {
          void this.ensureAgentOffBreak(false, { forceClear: true, loginBreakOnly: true });
          break;
        }
        if (this.userBreakArmed || isCubeBreakType(incomingBreak)) {
          this.userBreakArmed = true;
          this.autoClearBreakUntil = 0;
        }
        this.setUiStatus('on_break');
        {
          const armed =
            this.callState$.getValue()?.breakValue ||
            (isCubeBreakType(incomingBreak) ? incomingBreak : '');
          const breakValue =
            this.userBreakArmed && isCubeLoginBreak(incomingBreak)
              ? resolveCubeBreakType(armed || 'Tea Break')
              : resolveCubeBreakType(armed || incomingBreak || 'Tea Break');
          this.publishCallState({ Status: 'Break', pauseCall: true, breakValue });
        }
        this.emit(this.breakStarted$, data);
        break;
      }
      case 'BREAK_END':
      case 'BREAK_ENDED': {
        const endVia = String((data as { via?: string })?.via || '');
        if (this.userBreakArmed && (endVia === 'detect' || endVia === 'reassert')) {
          break;
        }
        if (this.userBreakArmed && endVia === 'BREAK_END_BLOCKED') {
          break;
        }
        this.userBreakArmed = false;
        if (this.agentLoggedIn) {
          this.setUiStatus('ready');
        }
        this.emit(this.breakEnded$, data);
        break;
      }
      case 'INBOUND_CALL': {
        const via = String((data as { via?: string })?.via || '');
        const direction = String((data as { callDirection?: string })?.callDirection || '');
        const cur = this.callState$.getValue();
        const idle =
          !cur ||
          cur.Status === 'Idle' ||
          cur.Status === 'Disconnected' ||
          cur.Status === 'Break';
        if (
          (via === 'callwatch' || via === 'sip_status' || direction === 'campaign') &&
          idle &&
          !this.agentAnsweredByUser
        ) {
          this.callSource = this.resolveInboundCallSource(data) || 'campaign';
          if (this.isDummyAutoTransferSession()) {
            this.callSource = 'campaign';
          }
          this.primeAgentAnswerRing(data);
          this.handleCallProgress(data, 'CALLING');
          break;
        }
        if (
          via === 'callwatch' ||
          via === 'sip_status' ||
          direction === 'campaign' ||
          this.callSource === 'campaign' ||
          !!cur?.customerAnswered
        ) {
          break;
        }
        if (cur?.Status === 'CONNECTED' || this.agentAnsweredByUser) {
          break;
        }
        this.callSource = this.resolveInboundCallSource(data);
        if (this.isDummyAutoTransferSession()) {
          this.callSource = 'campaign';
        }
        this.primeAgentAnswerRing(data);
        this.handleCallProgress(data, 'CALLING');
        break;
      }
      case 'CUSTOMER_ANSWERED':
      case 'CUSTOMER_CONNECTED':
      case 'AGENT_OFFERING': {
        const via = String((data as { via?: string })?.via || '');
        const phone = String(
          (data as { phoneNo?: string; phoneNumber?: string })?.phoneNo ||
            (data as { phoneNumber?: string })?.phoneNumber ||
            ''
        ).replace(/\D/g, '');
        const states = ((data as { states?: string[] })?.states || []).map((s) =>
          String(s || '').toUpperCase()
        );
        const hasOffer = states.some((s) => /OFFER|RING|CONNECTED|TALKING|INCALL/.test(s));
        const hasLive = states.some((s) => /CONNECTED|TALKING|INCALL/.test(s));
        if (via === 'callwatch' && phone.length < 10) {
          break;
        }
        if (via === 'callwatch' && !hasOffer && msgType !== 'AGENT_OFFERING') {
          break;
        }
        if (hasLive) {
          if (this.isDummyAutoTransferSession() && !this.cubeTransferDispatched) {
            if (this.callSource !== 'campaign') {
              this.callSource = 'campaign';
            }
            this.handleCampaignCustomerAnswered(data);
            break;
          }
          this.acceptIframeAgentConnection(data);
          break;
        }
        if (this.isDummyAutoTransferSession()) {
          if (this.callSource !== 'campaign') {
            this.callSource = 'campaign';
          }
          this.handleCampaignCustomerAnswered(data);
          break;
        }
        if (
          this.callSource === 'campaign' ||
          this.pendingUploadLeads.length > 0 ||
          String((data as { callDirection?: string })?.callDirection || '') === 'campaign'
        ) {
          if (this.callSource !== 'campaign' && this.pendingUploadLeads.length > 0 && !this.isManualDialerCall()) {
            this.setCallSourceCampaignIfAllowed();
          }
          if (this.callSource === 'campaign') {
            this.handleCampaignCustomerAnswered(data);
          } else {
            this.handleCallProgress(data, 'CONNECTED');
          }
          break;
        }
        this.handleCallProgress(data, 'CONNECTED');
        break;
      }
      case 'CALL_META': {
        const meta = this.extractCallMeta(data);
        if (meta.phoneNo || meta.dueId || meta.clientId) {
          const direction = this.isManualDialerCall()
            ? 'manual'
            : this.callSource === 'campaign'
              ? 'campaign'
              : this.callSource || undefined;
          if (this.callLegPhase === 'idle') {
            const legSource = this.isManualDialerCall() || direction === 'manual'
              ? 'manual'
              : direction === 'campaign' || this.pendingUploadLeads.length > 0
                ? 'campaign'
                : this.callSource || 'inbound';
            this.beginCallLeg(legSource);
          }
          if (
            !this.isManualDialerCall() &&
            (direction === 'campaign' || this.pendingUploadLeads.length > 0)
          ) {
            this.setCallSourceCampaignIfAllowed();
          }
          const nextStatus =
            this.callState$.getValue()?.Status === 'CONNECTED' ||
            this.cubeIframeAgentConnected ||
            this.agentAnsweredByUser
              ? 'CONNECTED'
              : 'CALLING';
          this.publishCallState({
            ...meta,
            customerAnswered: true,
            callDirection: direction || (this.callSource === 'campaign' ? 'campaign' : undefined),
            Status: nextStatus,
          });
          const state = this.callState$.getValue();
          if (state) {
            this.incomeCallService.setCallingRes({ ...state, callingType: 'cube' });
          }
          if (nextStatus === 'CONNECTED') {
            this.emit(this.callAnswered$, { type: 'CALL_META', ...meta });
            this.ensureOpenCaseFromConnected(meta);
          } else {
            this.emit(this.inboundCall$, data);
          }
        }
        break;
      }
      case 'CALL_ANSWERED':
      case 'ANSWER': {
        const via = String((data as { via?: string })?.via || '');
        const states = ((data as { states?: string[] })?.states || []).map((s) =>
          String(s || '').toUpperCase()
        );
        const reallyConnected =
          via === 'callwatch' && states.some((s) => /CONNECTED|TALKING|INCALL/.test(s));
        if (via !== 'callwatch' && via !== 'ui_interaction') {
          if (!this.callSource && this.pendingUploadLeads.length > 0) {
            this.callSource = 'campaign';
          }
          const cur = this.callState$.getValue();
          const canAcceptNative =
            !!cur?.customerAnswered ||
            this.cubeAwaitingAgentAnswer ||
            this.agentAnsweredByUser ||
            this.callLegPhase === 'active' ||
            this.isCampaignDialerCall() ||
            this.callSource === 'inbound' ||
            this.callSource === 'campaign';
          if (canAcceptNative) {
            this.acceptIframeAgentConnection(data);
          } else {
            this.handleCallProgress(data, 'CALLING');
            if (!this.cubeTransferRouting && getCubeAutoAnswerEnabled()) {
              this.maybeAutoAnswerCampaign();
            }
          }
          break;
        }
        if (!this.callSource && this.pendingUploadLeads.length > 0) {
          this.callSource = 'campaign';
        }
        if (this.isManualDialerCall() || this.callSource === 'manual') {
          if (this.isRemoteConnectedSignal(data) || reallyConnected) {
            this.acceptIframeAgentConnection(data);
            break;
          }
          this.handleCallProgress(data, 'CALLING');
          break;
        }
        if (this.isRemoteConnectedSignal(data) || reallyConnected) {
          this.acceptIframeAgentConnection(data);
          break;
        }
        if (
          (this.callSource === 'campaign' || this.pendingUploadLeads.length > 0) &&
          !this.callState$.getValue()?.customerAnswered
        ) {
          this.handleCampaignCustomerAnswered(data);
        }
        const liveState = this.callState$.getValue();
        if (
          this.agentAnsweredByUser ||
          liveState?.Status === 'CONNECTED' ||
          reallyConnected ||
          this.getCurrentStatus() === 'on_call'
        ) {
          this.cubeAwaitingAgentAnswer = false;
          this.handleCallProgress(data, 'CONNECTED');
          break;
        }
        if (!this.cubeAwaitingAgentAnswer && this.getCurrentStatus() !== 'on_call' && !this.agentAnsweredByUser) {
          this.cubeAwaitingAgentAnswer = true;
        }
        this.handleCallProgress(data, 'CALLING');
        if (!this.cubeTransferRouting && getCubeAutoAnswerEnabled()) {
          this.maybeAutoAnswerCampaign();
        }
        break;
      }
      case 'CALL_CONNECTED': {
        if (!this.callSource && this.pendingUploadLeads.length > 0) {
          this.callSource = 'campaign';
        }
        if (this.isManualDialerCall() || this.callSource === 'manual') {
          if (this.isRemoteConnectedSignal(data)) {
            this.acceptIframeAgentConnection(data);
            break;
          }
          this.handleCallProgress(data, 'CALLING');
          break;
        }
        if (this.isRemoteConnectedSignal(data)) {
          this.acceptIframeAgentConnection(data);
          break;
        }
        const liveState = this.callState$.getValue();
        if (this.agentAnsweredByUser || liveState?.Status === 'CONNECTED' || this.getCurrentStatus() === 'on_call') {
          this.cubeAwaitingAgentAnswer = false;
          this.handleCallProgress(data, 'CONNECTED');
          break;
        }
        if (!this.cubeAwaitingAgentAnswer && this.getCurrentStatus() !== 'on_call' && !this.agentAnsweredByUser) {
          this.cubeAwaitingAgentAnswer = true;
        }
        if (this.isCampaignDialerCall() && !liveState?.customerAnswered) {
          this.handleCampaignCustomerAnswered(data);
        } else {
          this.handleCallProgress(data, 'CALLING');
        }
        if (getCubeAutoAnswerEnabled() && this.isCampaignDialerCall()) {
          this.maybeAutoAnswerCampaign();
        }
        break;
      }
      case 'CONNECTED':
      case 'INCALL':
      case 'TALKING': {
        if (
          (this.callSource === 'manual' || this.isManualDialerCall()) &&
          !this.agentAnsweredByUser &&
          !this.cubeIframeAgentConnected
        ) {
          this.publishCallState(this.extractCallMeta(data));
          this.handleCallProgress(data, 'CALLING');
          break;
        }
        this.acceptIframeAgentConnection(data);
        break;
      }
      case 'SIP_UNAUTHORIZED': {
        const msg =
          this.extractLoginErrorMessage(data) ||
          'SIP unauthorized (267) — softphone not registered; tap Retry';
        if (this.agentLoggedIn) {
          this.cubeLog('handleWebphoneMessage', { sip: 'reregister', msg });
          this.sendIframeAction('REREGISTER_SIP');
          break;
        }
        this.lastLoginError = msg;
        this.ngZone.run(() => {
          this.loginFailed$.next(data);
          this.status$.next('failed');
          this.emitError(this.lastLoginError);
        });
        break;
      }
      case 'QCS_ERROR': {
        const msg =
          this.extractLoginErrorMessage(data) ||
          'Cube QCS disconnected (AgentStatus failed). Tap Retry.';
        const transientAgentStatus = /agentstatus|no response from server/i.test(msg);
        if (this.agentLoggedIn && transientAgentStatus) {
          this.cubeLog('handleWebphoneMessage', { skip: 'qcs_error_while_logged_in', msg });
          this.requestLoginStatePing();
          break;
        }
        if (!this.agentLoggedIn && this.softphoneReady && transientAgentStatus) {
          this.cubeLog('handleWebphoneMessage', { defer: 'qcs_error_ping_recovery', msg });
          this.requestLoginStatePing();
          setTimeout(() => {
            if (this.agentLoggedIn) {
              this.ngZone.run(() => {
                this.lastLoginError = '';
                this.status$.next('ready');
                this.loginState$.next(true);
              });
            }
          }, 1200);
          break;
        }
        this.agentLoggedIn = false;
        this.loginInFlight = false;
        this.agentAnsweredByUser = false;
        this.cubeAwaitingAgentAnswer = false;
        this.callSource = null;
        this.lastLoginError = msg;
        this.publishCallState({
          Status: 'Idle',
          customerAnswered: false,
          phoneNo: '',
          holdCall: false,
          pauseCall: false,
        });
        this.ngZone.run(() => {
          this.loginState$.next(false);
          this.loginFailed$.next(data);
          this.status$.next('failed');
          this.emitError(this.lastLoginError);
        });
        break;
      }
      case 'DISPOSITION_NOT_FOUND': {
        if (!this.callLegEverConnected) {
          break;
        }
        const badDisp = String((data as { disposition?: string })?.disposition || '').toLowerCase();
        if (badDisp !== CUBE_DEFAULT_DISPOSITION.toLowerCase()) {
          setTimeout(() => void this.endActiveCall(CUBE_DEFAULT_DISPOSITION, true), 200);
        }
        break;
      }
      case 'DISPOSE_DONE':
        this.signalFreemeDone();
        break;
      case 'CALL_ENDED':
      case 'HANGUP':
      case 'CALL_DISCONNECTED': {
        if (!this.callLegEverConnected) {
          this.cubeLog('handleWebphoneMessage', { skip: 'end_before_connected', type: msgType });
          break;
        }
        this.lastCallEndedAt = Date.now();
        this.handleRemoteCallEnd(data);
        break;
      }
      case 'WRAP_UP':
        if (this.callDisposeInFlight) {
          this.signalWrapUpReady();
          break;
        }
        if (!this.callLegEverConnected) {
          this.cubeLog('handleWebphoneMessage', { skip: 'premature_wrap_up' });
          break;
        }
        this.lastCallEndedAt = Date.now();
        this.handleRemoteCallEnd(data);
        break;
      case 'OUTBOUND_CALL':
        if (this.callSource !== 'manual') {
          this.setCallSourceCampaignIfAllowed();
        }
        this.handleCallProgress(data, 'CALLING');
        break;
      case 'CALL_STARTED':
        if (!this.isManualDialerCall()) {
          this.setCallSourceCampaignIfAllowed();
        }
        this.handleCallProgress(data, 'CALLING');
        break;
      case 'OUTBOUND_MANUAL_CALL':
        this.callSource = 'manual';
        this.handleCallProgress(data, 'CALLING');
        break;
      default:
        break;
    }
  }

  private markReady(data: unknown): void {
    if (this.softphoneReady) {
      return;
    }
    this.softphoneReady = true;
    this.emit(this.ready$, data);
    this.applyConfiguredAutoAnswer();
    setTimeout(() => {
      if (!this.agentLoggedIn) {
        void this.sendLoginToIframe(true, true);
      }
    }, 1500);
    this.scheduleLoginFallback();
    this.startLoginStatePoll();
  }

  private markLoggedIn(data: unknown): void {
    const already = this.agentLoggedIn;
    this.agentLoggedIn = true;
    this.loginInFlight = false;
    this.duplicateSessionRetryCount = 0;
    this.lastLoginError = '';
    this.clearLoginFlightTimer();
    this.clearLoginAckTimer();
    this.clearLoginFallbackTimer();
    this.clearLoginStatePoll();
    const authId = this.extractAuthId(data);
    if (authId) {
      this.syncAuthIdToServer(authId, data);
    }
    this.ngZone.run(() => {
      this.loginState$.next(true);
      if (!already) {
        this.loggedIn$.next(data);
      }
      this.status$.next('ready');
    });
    this.applyConfiguredAutoAnswer();
    if (!already) {
      setTimeout(() => this.sendIframeAction('REREGISTER_SIP'), 3500);
      this.autoClearBreakUntil = Date.now() + 25000;
      void this.bootstrapRestAuthId();
      if (shouldUseCubeSdk()) {
        this.scheduleSdkBreakEndAfterLogin();
      }
      if (this.pendingUploadLeads.length) {
        setTimeout(() => void this.handleCampaignDialNudge(undefined, this.pendingUploadLeads), 2000);
      }
    }
  }

  private async bootstrapRestAuthId(): Promise<void> {
    try {
      await firstValueFrom(this.callingService.bootstrapCubeConnectorSession());
    } catch (e) {
      console.warn('[CubeConnector] REST AuthId bootstrap failed', e);
    }
  }

  private markLoginFailed(data: unknown): void {
    this.clearLoginAckTimer();
    this.agentLoggedIn = false;
    this.loginInFlight = false;
    this.clearLoginFlightTimer();
    this.lastLoginError = this.extractLoginErrorMessage(data);

    if (
      this.isDuplicateSessionError(data) &&
      this.duplicateSessionRetryCount < this.maxDuplicateSessionRetries
    ) {
      this.duplicateSessionRetryCount += 1;
      const delayMs = 2500 + this.duplicateSessionRetryCount * 1500;
      console.warn(
        `[CubeConnector] Duplicate session — force logout + retry ${this.duplicateSessionRetryCount}/${this.maxDuplicateSessionRetries}`,
        this.lastLoginError
      );
      this.ngZone.run(() => {
        this.loginState$.next(false);
        this.setUiStatus('logging_in');
      });
      void this.retryLoginAfterForceLogout(delayMs);
      return;
    }

    this.ngZone.run(() => {
      this.loginState$.next(false);
      this.loginFailed$.next(data);
      this.status$.next('failed');
      this.emitError(this.lastLoginError);
    });
  }

  private markLoggedOut(data: unknown): void {
    this.agentLoggedIn = false;
    this.loginInFlight = false;
    this.clearLoginAckTimer();
    this.clearLoginStatePoll();
    this.ngZone.run(() => {
      this.loginState$.next(false);
      this.loggedOut$.next(data);
      this.status$.next('logged_out');
    });
  }

  private scheduleLoginAck(): void {
    this.clearLoginAckTimer();
    this.loginAckTimer = setTimeout(() => {
      if (!this.agentLoggedIn && this.loginInFlight) {
        this.loginInFlight = false;
        void this.sendLoginToIframe(true, true);
      }
    }, 12000);
  }

  private markLoginInFlight(): void {
    this.loginInFlight = true;
    this.setUiStatus('logging_in');
    this.clearLoginFlightTimer();
    this.startLoginStatePoll();
    this.loginFlightTimer = setTimeout(() => {
      if (!this.agentLoggedIn) {
        this.loginInFlight = false;
        this.pingIframeLoginState();
      }
    }, 10000);
    this.scheduleLoginAck();
    setTimeout(() => {
      if (!this.agentLoggedIn && this.getCurrentStatus() === 'logging_in') {
        this.markLoginFailed({
          reason: 'Cube login timed out. Tap Retry to reconnect.',
          via: 'login_timeout',
        });
      }
    }, 45000);
  }

  private emit(subject: Subject<unknown>, data: unknown): void {
    this.ngZone.run(() => subject.next(data));
  }

  private emitError(msg: string): void {
    this.ngZone.run(() => this.error$.next(msg));
  }

  private agentPayload(agent: { agentId: string; extension: string; password: string; campaignId: string }) {
    const campaign = String(agent.campaignId || '').trim();
    return {
      agentId: agent.agentId,
      agentID: agent.agentId,
      extension: agent.extension,
      agentExtn: agent.extension,
      password: agent.password,
      campaignId: campaign,
      campaignID: campaign,
      CampaignId: campaign,
      CampaignID: campaign,
    };
  }

  private postForceLogoutToIframe(agent: { agentId: string }): boolean {
    return this.sendIframeAction('LOGOUT', { force: true, agentId: agent.agentId, agentID: agent.agentId });
  }

  private async retryLoginAfterForceLogout(delayMs: number): Promise<void> {
    if (shouldUseCubeSdk()) {
      await new Promise((resolve) => setTimeout(resolve, delayMs));
      if (!this.agentLoggedIn) {
        await this.loginViaSdk(true);
      }
      return;
    }
    const agent = await this.resolveAgentConfig(true);
    if (!isCubeAgentConfigComplete(agent)) {
      this.ngZone.run(() => {
        this.loginState$.next(false);
        this.status$.next('failed');
        this.emitError(this.lastLoginError || 'Cube credentials missing');
      });
      return;
    }
    this.postForceLogoutToIframe(agent);
    if (this.connector?.logout) {
      this.connector.logout();
    }
    this.sendIframeAction('LOGOUT', { force: true, agentId: agent.agentId, agentID: agent.agentId });
    await new Promise((resolve) => setTimeout(resolve, delayMs));
    if (!this.agentLoggedIn) {
      this.preLoginLogoutDone = true;
      await this.sendLoginToIframe(true, true);
    }
  }

  private postLoginToIframe(agent: { agentId: string; extension: string; password: string; campaignId: string }, force = false): boolean {
    const payload = this.agentPayload(agent);
    return this.postToCubeIframe({ source: 'SF_ADAPTER', type: 'LOGIN', force: !!force, ...payload });
  }

  private async resolveAgentConfig(forceRefresh = false) {
    return this.cubeAgentConfig.ensureLoaded(forceRefresh);
  }

  private applyAgentToConnector(agent: { agentId: string; extension: string; password: string; campaignId: string } | null): boolean {
    if (!this.connector || !agent) {
      return false;
    }
    this.connector.setAgent(this.agentPayload(agent));
    return true;
  }

  private buildConnectorOptions(agent: Record<string, string>) {
    const origin = getCubeConnectorChildOrigin();
    const handler = (event: string, subject: Subject<unknown>) => (data: unknown) => {
      switch (event) {
        case 'onReady':
          this.markReady(data);
          return;
        case 'onLoggedIn':
          this.markLoggedIn(data);
          return;
        case 'onLoginFailed':
          this.markLoginFailed(data);
          return;
        case 'onLoggedOut':
          this.markLoggedOut(data);
          return;
        default:
          this.emit(subject, data);
      }
    };

    return {
      childOrigin: origin,
      iframeSrcOrigin: origin,
      iframeTargetOrigin: origin,
      autoLogin: false,
      agent,
      onReady: handler('onReady', this.ready$),
      onIframeLoad: () => {
      },
      onLoggedIn: handler('onLoggedIn', this.loggedIn$),
      onLoginFailed: handler('onLoginFailed', this.loginFailed$),
      onLoggedOut: handler('onLoggedOut', this.loggedOut$),
      onInboundCall: (data: unknown) => {
        handler('onInboundCall', this.inboundCall$)(data);
        this.callSource = this.resolveInboundCallSource(data);
        if (this.isDummyAutoTransferSession()) {
          this.callSource = 'campaign';
        }
        this.primeAgentAnswerRing(data);
        this.handleCallProgress(data, 'CALLING');
      },
      onOutboundCall: (data: unknown) => {
        handler('onOutboundCall', this.inboundCall$)(data);
        if (!this.isManualDialerCall()) {
          this.setCallSourceCampaignIfAllowed();
        }
        this.handleCallProgress(data, 'CALLING');
      },
      onCallStarted: (data: unknown) => {
        handler('onCallStarted', this.inboundCall$)(data);
        if (!this.isManualDialerCall()) {
          this.setCallSourceCampaignIfAllowed();
        }
        this.handleCallProgress(data, 'CALLING');
      },
      onCallAnswered: (data: unknown) => {
        handler('onCallAnswered', this.callAnswered$)(data);
        if (this.callEndInProgress || this.callLegCancelled) {
          this.tearDownCallLeg();
          return;
        }
        if (!this.agentAnsweredByUser) {
          this.cubeAwaitingAgentAnswer = true;
          this.handleCallProgress(data, 'CALLING');
          return;
        }
        this.cubeAwaitingAgentAnswer = false;
        this.handleCallProgress({ ...(data as object), type: 'CALL_ANSWERED' }, 'CONNECTED');
      },
      onCallEnded: (data: unknown) => {
        this.handleRemoteCallEnd(data);
      },
      onBreakStarted: (data: unknown) => {
        handler('onBreakStarted', this.breakStarted$)(data);
        const incomingBreak = this.extractIncomingBreakLabel(data);
        if (this.shouldAutoClearLoginBreak(incomingBreak)) {
          void this.ensureAgentOffBreak(false, { forceClear: true, loginBreakOnly: true });
          return;
        }
        if (this.userBreakArmed || isCubeBreakType(incomingBreak)) {
          this.userBreakArmed = true;
          this.autoClearBreakUntil = 0;
        }
        this.setUiStatus('on_break');
        const armed =
          this.callState$.getValue()?.breakValue ||
          (isCubeBreakType(incomingBreak) ? incomingBreak : '');
        const breakValue =
          this.userBreakArmed && isCubeLoginBreak(incomingBreak)
            ? resolveCubeBreakType(armed || 'Tea Break')
            : resolveCubeBreakType(armed || incomingBreak || 'Tea Break');
        this.publishCallState({ Status: 'Break', pauseCall: true, breakValue });
      },
      onBreakEnded: (data: unknown) => {
        handler('onBreakEnded', this.breakEnded$)(data);
        const endVia = String((data as { via?: string })?.via || '');
        if (this.userBreakArmed && (endVia === 'detect' || endVia === 'reassert' || endVia === 'BREAK_END_BLOCKED')) {
          return;
        }
        this.userBreakArmed = false;
        if (this.agentLoggedIn) {
          this.setUiStatus('ready');
        }
      },
      onError: (data: unknown) => {
        const msg = typeof data === 'string' ? data : JSON.stringify(data);
        console.error('[CubeConnector] onError', data);
        this.emitError(msg);
      },
    };
  }

  async init(iframeId: string): Promise<void> {
    if (shouldUseCubeSdk()) {
      return this.initViaSdk(iframeId);
    }
    this.teardownDone = false;
    this.duplicateSessionRetryCount = 0;
    this.lastLoginError = '';
    this.setUiStatus('loading_webphone');

    let agent = await this.resolveAgentConfig(false);
    if (!isCubeAgentConfigComplete(agent)) {
      agent = await this.resolveAgentConfig(true);
    }
    if (!isCubeAgentConfigComplete(agent)) {
      const missing = describeMissingCubeCredentials();
      throw new Error(missing.length ? missing.join(', ') : 'Cube credentials not found in DB');
    }

    await this.ensureScript();
    if (!window.CubeConnector) {
      throw new Error('CubeConnector SDK not available');
    }

    this.installMessageBridge();
    this.installUploadDialerWatch();
    await this.waitForIframe(iframeId);

    if (this.connector) {
      this.applyAgentToConnector(agent);
      if (!this.agentLoggedIn) {
        this.scheduleLoginFallback();
      }
      return;
    }

    this.connector = new window.CubeConnector!(iframeId, this.buildConnectorOptions(this.agentPayload(agent)));
    this.scheduleLoginFallback();
  }

  private async sendLoginToIframe(force = false, refreshAgent = false): Promise<boolean> {
    if (this.agentLoggedIn) {
      return false;
    }
    if (!force && !this.softphoneReady) {
      return false;
    }
    const now = Date.now();
    if (!force && now - this.lastLoginPushAt < 2000) {
      return false;
    }
    if (this.loginInFlight && !force) {
      return false;
    }

    const agent = await this.resolveAgentConfig(refreshAgent);
    if (!isCubeAgentConfigComplete(agent)) {
      const missing = describeMissingCubeCredentials();
      console.warn('[CubeConnector] cubeDetails incomplete — cannot login', missing);
      this.emitError(
        missing.length
          ? `Cube login credentials missing: ${missing.join(', ')}`
          : 'Cube agent credentials not found. Re-login to Moneytor.'
      );
      return false;
    }

    if (this.connector) {
      this.applyAgentToConnector(agent);
    }

    if (!this.preLoginLogoutDone) {
      this.preLoginLogoutDone = true;
      this.postForceLogoutToIframe(agent);
      await new Promise((resolve) => setTimeout(resolve, 2000));
    }

    const postOk = this.postLoginToIframe(agent, force);
    const connectorOk = postOk ? false : (this.connector?.login() ?? false);
    const ok = postOk || connectorOk;
    if (!ok) {
      this.setUiStatus('failed');
      this.emitError('Cube iframe not ready — could not send LOGIN');
      return false;
    }
    this.lastLoginPushAt = now;
    this.markLoginInFlight();
    return true;
  }

  private async reloadCubeIframeForLogin(): Promise<boolean> {
    const iframe = document.getElementById(getCubeBridgeIframeId()) as HTMLIFrameElement | null;
    if (!iframe) {
      return false;
    }
    this.softphoneReady = false;
    const base = getCubeWebphoneUrl();
    const next = new URL(base, window.location.href);
    next.searchParams.set('cubePx', String(Date.now()));
    next.searchParams.set('_r', String(Date.now()));
    iframe.src = next.toString();
    return new Promise<boolean>((resolve) => {
      let settled = false;
      const finish = (ok: boolean) => {
        if (settled) {
          return;
        }
        settled = true;
        clearTimeout(timer);
        sub.unsubscribe();
        resolve(ok);
      };
      const timer = setTimeout(() => finish(this.softphoneReady), 20000);
      const sub = this.ready$.subscribe(() => {
        if (this.softphoneReady) {
          finish(true);
        }
      });
    });
  }

  async forceLogin(): Promise<boolean> {
    if (shouldUseCubeSdk()) {
      return this.forceLoginViaSdk();
    }
    this.agentLoggedIn = false;
    this.loginInFlight = false;
    this.clearLoginFlightTimer();
    this.clearLoginAckTimer();
    this.clearLoginRequestTimer();
    this.clearLoginFallbackTimer();
    this.duplicateSessionRetryCount = 0;
    this.lastLoginError = '';
    this.preLoginLogoutDone = false;
    this.lastLoginPushAt = 0;
    this.callSource = null;
    this.agentAnsweredByUser = false;
    this.cubeAwaitingAgentAnswer = false;
    this.callLegCancelled = false;
    this.callEndInProgress = false;
    this.callDisposeInFlight = false;
    this.pendingCampaignMeta = {};
    this.publishCallState({
      Status: 'Idle',
      customerAnswered: false,
      phoneNo: '',
      holdCall: false,
      pauseCall: false,
      callDirection: undefined,
    });
    this.loginState$.next(false);
    this.setUiStatus('logging_in');
    const agent = await this.resolveAgentConfig(true);
    if (isCubeAgentConfigComplete(agent)) {
      this.postForceLogoutToIframe(agent);
      this.sendIframeAction('LOGOUT', { force: true, agentId: agent.agentId, agentID: agent.agentId });
      if (this.connector?.logout) {
        this.connector.logout();
      }
      await new Promise((resolve) => setTimeout(resolve, 800));
    }
    const iframeReady = await this.reloadCubeIframeForLogin();
    if (!iframeReady) {
      this.markLoginFailed({
        reason: 'Cube iframe failed to load. Tap Retry to reconnect.',
        via: 'iframe_reload',
      });
      return false;
    }
    this.preLoginLogoutDone = true;
    return this.sendLoginToIframe(true, true);
  }

  private postToCubeIframe(msg: Record<string, unknown>): boolean {
    if (shouldUseCubeSdk()) {
      return false;
    }
    const iframe = document.getElementById(getCubeBridgeIframeId()) as HTMLIFrameElement | null;
    if (!iframe?.contentWindow) {
      this.emitError('Cube iframe not ready');
      return false;
    }
    const target = getCubeIframePostMessageTarget(iframe) || '*';
    try {
      iframe.contentWindow.postMessage(msg, target);
      return true;
    } catch (_e) {
      try {
        iframe.contentWindow.postMessage(msg, '*');
        return true;
      } catch (_e2) {
        this.emitError('Failed to postMessage to Cube iframe');
        return false;
      }
    }
  }

  private sendIframeAction(type: string, extra: Record<string, unknown> = {}): boolean {
    return this.postToCubeIframe({ source: 'SF_ADAPTER', type, ...extra });
  }

  makeCall(phoneNumber: string, context?: Partial<CubeCallUiState>): boolean {
    if (shouldUseCubeSdk()) {
      return this.makeCallViaSdk(phoneNumber, context);
    }
    if (!this.connector && !document.getElementById(getCubeBridgeIframeId())) {
      this.emitError('Cube webphone not initialized');
      return false;
    }
    if (!this.agentLoggedIn) {
      this.emitError('Cube webphone not logged in');
      return false;
    }
    const number = String(phoneNumber || '').replace(/\D/g, '').slice(-10);
    if (!number || number.length < 10) {
      this.emitError('Invalid phone number');
      return false;
    }
    console.log('[CubeConnector] makeCall', number, context);
    this.callEndInProgress = false;
    this.callLegCancelled = false;
    this.cubeIframeIdleTicks = 0;
    this.cubeWrapUpPending = false;
    const isManual =
      (context as { callDirection?: string })?.callDirection !== 'campaign';
    this.beginCallLeg(isManual ? 'manual' : 'campaign');
    this.agentAnsweredByUser = false;
    this.cubeIframeAgentConnected = false;
    this.callLegEverConnected = false;
    this.outboundDialSent = false;
    this.cubeAwaitingAgentAnswer = !isManual;
    if (isManual) {
      this.clearAutoAnswerPromoteTimer();
      this.clearConnectTimer();
    }
    this.publishCallState({
      Status: 'CALLING',
      phoneNo: number,
      phoneNumber: number,
      callDirection: isManual ? 'manual' : 'campaign',
      customerAnswered: false,
      ...(context || {}),
    });
    if (context?.fullName) {
      this.rememberCallerName(number, context.fullName);
    }
    if (context?.clientId) {
      localStorage.setItem('clientId', String(context.clientId));
    }
    localStorage.setItem('incomingCall', 'true');
    try {
      localStorage.setItem('callingType', 'cube');
    } catch (_e) {}
    this.incomeCallService.setCallingRes({
      ...this.callState$.getValue(),
      callingType: 'cube',
      Status: 'CALLING',
      phoneNo: number,
      phoneNumber: number,
      fullName: context?.fullName || this.callState$.getValue()?.fullName || '',
    });
    this.setUiStatus('on_call');
    void this.placeOutboundCall(number, context);
    return true;
  }

  private async placeOutboundCall(number: string, context?: Partial<CubeCallUiState>): Promise<boolean> {
    if (this.isAgentOnBreak() || this.getCurrentStatus() === 'on_break') {
      await this.ensureAgentOffBreak(true, { forceClear: true });
      await new Promise((r) => setTimeout(r, 400));
    }

    const ok =
      this.sendIframeAction('OUTBOUND_CALL', {
        number,
        phoneNumber: number,
        phone: number,
        PhoneNo: number,
        telNumber: number,
        context: context || {},
      }) ||
      this.sendIframeAction('MAKE_CALL', {
        number,
        phoneNumber: number,
        phone: number,
        PhoneNo: number,
        telNumber: number,
        context: context || {},
      });

    if (!ok) {
      this.emitError('Failed to place call — webphone iframe not ready');
      this.endCallLegToIdle();
      this.cubeAwaitingAgentAnswer = false;
      if (this.agentLoggedIn) {
        this.setUiStatus('ready');
      }
      this.ngZone.run(() => {
        this.callState$.next({
          callingType: 'cube',
          Status: 'Idle',
          holdCall: false,
          customerAnswered: false,
          phoneNo: '',
        });
      });
      return false;
    }
    this.outboundDialSent = true;
    console.log('[CubeConnector] OUTBOUND_CALL sent', number);
    return true;
  }

  private extractBreakLabel(data?: unknown): string {
    const d = (data || {}) as {
      breakType?: string;
      breakInfo?: string;
      BreakType?: string;
    };
    const fromData = String(d.breakType || d.breakInfo || d.BreakType || '').trim();
    if (fromData) {
      return fromData;
    }
    return String(this.callState$.getValue()?.breakValue || '').trim();
  }

  private extractIncomingBreakLabel(data?: unknown): string {
    const d = (data || {}) as {
      breakType?: string;
      breakInfo?: string;
      BreakType?: string;
    };
    return String(d.breakType || d.breakInfo || d.BreakType || '').trim();
  }

  private shouldAutoClearLoginBreak(label?: string): boolean {
    if (this.userBreakArmed) {
      return false;
    }
    const active = this.callState$.getValue()?.breakValue;
    if (isCubeBreakType(active) && !isCubeLoginBreak(active)) {
      return false;
    }
    if (Date.now() >= this.autoClearBreakUntil) {
      return false;
    }
    if (isCubeBreakType(label) && !isCubeLoginBreak(label)) {
      return false;
    }
    return isCubeLoginBreak(label) || !label;
  }

  private async ensureAgentOffBreak(
    preserveCallState = false,
    opts: { forceClear?: boolean; loginBreakOnly?: boolean } = {}
  ): Promise<boolean> {
    const forceClear = !!opts.forceClear;
    const loginBreakOnly = !!opts.loginBreakOnly;
    if (this.userBreakArmed && loginBreakOnly) {
      return true;
    }
    if (loginBreakOnly) {
      const active = this.extractBreakLabel();
      if (isCubeBreakType(active) && !isCubeLoginBreak(active)) {
        return true;
      }
      if (!forceClear) {
        const onBreak =
          this.getCurrentStatus() === 'on_break' ||
          !!this.callState$.getValue()?.pauseCall ||
          this.callState$.getValue()?.Status === 'Break';
        if (!onBreak) {
          return true;
        }
        if (active && !isCubeLoginBreak(active)) {
          return true;
        }
      }
    }
    const onBreak =
      this.getCurrentStatus() === 'on_break' ||
      !!this.callState$.getValue()?.pauseCall ||
      this.callState$.getValue()?.Status === 'Break';
    if (!onBreak && !forceClear) {
      return true;
    }
    if (loginBreakOnly && forceClear && this.userBreakArmed) {
      return true;
    }
    let ok = this.connector?.breakEnd?.() ?? false;
    if (!ok) {
      ok = this.sendIframeAction('BREAK_END');
    }
    if (ok || this.agentLoggedIn) {
      if (!loginBreakOnly) {
        this.userBreakArmed = false;
      }
      const callStatus = this.callState$.getValue()?.Status;
      if (!preserveCallState && callStatus !== 'CALLING' && callStatus !== 'CONNECTED') {
        this.setUiStatus('ready');
        this.publishCallState({ Status: 'Idle', pauseCall: false, breakValue: '' });
      } else if (preserveCallState) {
        this.publishCallState({ pauseCall: false, breakValue: '' });
      } else {
        this.setUiStatus('ready');
      }
    }
    return ok || this.agentLoggedIn;
  }

  hangup(): boolean {
    if (this.cubeWrapUpPending || this.callDisposeInFlight || this.callLegPhase === 'wrap_up') {
      return true;
    }
    if (shouldUseCubeSdk()) {
      // Mark cancelled before hangup() — Cube may fire onCallEnded synchronously.
      this.callLegCancelled = true;
      this.cubeSdk.hangup();
      if (this.cubeWrapUpPending || this.callDisposeInFlight) {
        return true;
      }
    }
    const snapshot = { ...(this.callState$.getValue() || { callingType: 'cube' as const }) };
    const shouldWrap =
      this.canShowDisposition('agent_hangup') ||
      this.callLegEverConnected ||
      this.outboundDialSent ||
      this.callLegPhase === 'active' ||
      snapshot.Status === 'CALLING' ||
      snapshot.Status === 'CONNECTED';
    this.callLegCancelled = true;
    this.cubeAwaitingAgentAnswer = false;
    this.cubeIframeAgentConnected = false;
    this.agentAnsweredByUser = false;
    this.tearDownCallLeg();
    if (shouldWrap) {
      this.beginWrapUp({ type: 'HANGUP', reason: 'agent_hangup', snapshot });
    } else {
      this.endCallLegToIdle();
      this.markCallEnded();
    }
    return true;
  }

  armWrapUpFromUi(snapshot?: Partial<CubeCallUiState>): void {
    if (this.cubeWrapUpPending) {
      return;
    }
    if (!this.canShowDisposition('ui_arm')) {
      return;
    }
    this.beginWrapUp({
      type: 'WRAP_UP',
      reason: 'ui_arm',
      snapshot: snapshot || this.callState$.getValue(),
    });
  }

  private async tryRestAction(
    request: Observable<{ resultShort?: string; resultLong?: string }>
  ): Promise<boolean> {
    try {
      const res = await firstValueFrom(request) as { resultShort?: string };
      return res?.resultShort !== 'failure';
    } catch (_e) {
      return false;
    }
  }

  private async restAction(
    request: Observable<{ resultShort?: string; resultLong?: string }>,
    okMessage: string,
    failMessage: string
  ): Promise<boolean> {
    if (!this.agentLoggedIn) {
      this.emitError('Cube webphone not logged in');
      return false;
    }
    try {
      const res = await firstValueFrom(request) as { resultShort?: string; resultLong?: string };
      if (res?.resultShort === 'failure') {
        this.emitError(res.resultLong || failMessage);
        return false;
      }
      console.log('[CubeConnector]', okMessage, res);
      return true;
    } catch (e: unknown) {
      const err = e as { error?: { resultLong?: string }; message?: string };
      this.emitError(err?.error?.resultLong || err?.message || failMessage);
      return false;
    }
  }

  private async iframeCallControl(type: string, extra: Record<string, unknown> = {}): Promise<boolean> {
    if (!this.sendIframeAction(type, extra)) {
      return false;
    }
    const waitMs = type === 'TRANSFER_CALL' ? 1200 : 400;
    await new Promise((resolve) => setTimeout(resolve, waitMs));
    return true;
  }

  async holdCall(): Promise<boolean> {
    if (shouldUseCubeSdk()) {
      return this.holdCallViaSdk();
    }
    if (this.callControlInFlight) {
      return false;
    }
    if (!this.isOnActiveCall()) {
      this.emitError('Hold is available during an active call');
      return false;
    }
    this.callControlInFlight = true;
    try {
      let ok = false;
      if (!isCubeWebphoneConnector() && (await this.hasRestAuthId())) {
        ok = await this.tryRestAction(this.callingService.holdCubeCall());
      }
      if (!ok) {
        ok = await this.iframeCallControl('HOLD_CALL', {
          callHandle: this.activeCallHandle || this.extractCallHandle(this.callState$.getValue()),
        });
      }
      if (ok) {
        this.publishCallState({ holdCall: true });
      } else {
        this.emitError('Failed to hold call');
      }
      return ok;
    } finally {
      this.callControlInFlight = false;
    }
  }

  async unholdCall(): Promise<boolean> {
    if (shouldUseCubeSdk()) {
      return this.unholdCallViaSdk();
    }
    if (this.callControlInFlight) {
      return false;
    }
    if (!this.isOnActiveCall()) {
      this.emitError('No active call to resume');
      return false;
    }
    this.callControlInFlight = true;
    try {
      let ok = false;
      if (!isCubeWebphoneConnector() && (await this.hasRestAuthId())) {
        ok = await this.tryRestAction(this.callingService.unholdCubeCall());
      }
      if (!ok) {
        ok = await this.iframeCallControl('UNHOLD_CALL', {
          callHandle: this.activeCallHandle || this.extractCallHandle(this.callState$.getValue()),
        });
      }
      if (ok) {
        this.publishCallState({ holdCall: false });
      } else {
        this.emitError('Failed to resume call');
      }
      return ok;
    } finally {
      this.callControlInFlight = false;
    }
  }

  async mute(): Promise<boolean> {
    if (shouldUseCubeSdk()) {
      return this.muteViaSdk();
    }
    if (!this.isOnActiveCall()) {
      this.emitError('Mute is available during an active call');
      return false;
    }
    const ok = await this.iframeCallControl('MUTE_CALL');
    if (!ok) {
      this.emitError('Failed to mute call');
    }
    return ok;
  }

  async unmute(): Promise<boolean> {
    if (shouldUseCubeSdk()) {
      return this.unmuteViaSdk();
    }
    if (!this.isOnActiveCall()) {
      return false;
    }
    const ok = await this.iframeCallControl('UNMUTE_CALL');
    if (!ok) {
      this.emitError('Failed to unmute call');
    }
    return ok;
  }

  sendDtmf(_digit: string): boolean {
    this.emitError('DTMF is not available from the widget in connector mode');
    return false;
  }

  async transfer(
    target: string,
    options?: { extension?: string; campaignId?: string; allowOffering?: boolean }
  ): Promise<boolean> {
    // SDK agent hops: CubeConnector.redirect(agentId)
    if (shouldUseCubeSdk()) {
      return this.redirectViaSdk(target, options);
    }
    const value = String(target || '').trim();
    const extension = String(options?.extension || '').trim();
    const campaignId = String(options?.campaignId || '').trim();
    const allowOffering = !!options?.allowOffering;
    this.cubeLog('transfer', { target: value, extension, campaignId, allowOffering });
    if (!value) {
      this.emitError('Enter agent ID to transfer to');
      return false;
    }
    if (this.cubeTransferInFlight) {
      return false;
    }
    this.cubeTransferInFlight = true;
    try {
      const state = this.callState$.getValue();
      const connected = this.isOnConnectedCall();
      const blindOffer =
        !!allowOffering &&
        !connected &&
        (this.cubeAwaitingAgentAnswer ||
          (state?.Status === 'CALLING' && !!state?.customerAnswered));
      if (!connected && !blindOffer) {
        this.emitError('Transfer is available during a connected call');
        return false;
      }
      if (!allowOffering && this.isCallOnHold()) {
        const resumed = await this.unholdCall();
        if (!resumed) {
          this.emitError('Resume the call before transfer');
          return false;
        }
        await new Promise((resolve) => setTimeout(resolve, 800));
      }
      if (isCubeWebphoneConnector()) {
        if (!this.transferArmSent) {
          this.armTransferRouting();
        }
        const callHandle = this.activeCallHandle || this.extractCallHandle(state);
        const payload = {
          agentId: value,
          agentID: value,
          redirectTo: value,
          RedirectToAgentId: value,
          extension,
          Extension: extension,
          agentExtn: extension,
          ConnectedWith: extension,
          campaignId,
          CampaignId: campaignId,
          redirectCampaign: campaignId,
          message: campaignId || '',
          transferMessage: campaignId || '',
          callHandle,
          allowOffering: blindOffer || undefined,
          blindOnOffer: blindOffer || undefined,
        };
        const resultWait = this.waitTransferResult(blindOffer ? 12000 : 10000);
        if (!this.sendIframeAction('TRANSFER_CALL', payload)) {
          this.cancelTransferResultWait(false);
          return false;
        }
        this.cubeTransferDispatched = true;
        const ok = await resultWait;
        if (ok && !blindOffer && this.isOnConnectedCall()) {
          await this.completeTransferIframe();
        }
        return ok;
      }
      if (await this.hasRestAuthId()) {
        const restState = this.callState$.getValue();
        return this.restAction(
          this.callingService.transferCall({
            RedirectToAgentId: value,
            clientId: restState?.clientId,
            mobileNum: restState?.phoneNo,
            Name: restState?.fullName,
          }),
          'transfer via REST',
          'Transfer failed'
        );
      }
      this.emitError('Transfer failed — Cube webphone not ready');
      return false;
    } finally {
      this.cubeTransferInFlight = false;
      this.cubeLog('transfer', { done: true, dispatched: this.cubeTransferDispatched });
    }
  }

  async redirect(
    target: string,
    options?: { extension?: string; campaignId?: string; allowOffering?: boolean }
  ): Promise<boolean> {
    // Widget Transfer + dummy→real auto-transfer.
    if (shouldUseCubeSdk()) {
      return this.redirectViaSdk(target, options);
    }
    return this.transfer(target, options);
  }

  async conference(phoneNumber: string): Promise<boolean> {
    const number = String(phoneNumber || '').replace(/\D/g, '').slice(-10);
    if (number.length < 10) {
      this.emitError('Invalid conference number');
      return false;
    }
    if (!this.isOnConnectedCall()) {
      this.emitError('Conference is available during a connected call');
      return false;
    }
    if (this.isAgentOnBreak()) {
      this.emitError('End your break before dialling a 3rd party');
      return false;
    }
    if (this.isCallOnHold()) {
      const resumed = await this.unholdCall();
      if (!resumed) {
        this.emitError('Resume the call before dialling a 3rd party');
        return false;
      }
    }
    if (isCubeWebphoneConnector()) {
      await this.iframeCallControl('UNHOLD_CALL');
      await new Promise((resolve) => setTimeout(resolve, 1200));
    }
    try {
      await firstValueFrom(this.callingService.bootstrapCubeConnectorSession());
    } catch (_e) { }
    if (!(await this.hasRestAuthId())) {
      await this.bootstrapRestAuthId();
    }
    let ok = await this.restAction(
      this.callingService.conferenceCall({ PhoneNo: number }),
      'conference via REST',
      'Conference dial failed'
    );
    if (!ok && isCubeWebphoneConnector()) {
      ok = await this.iframeCallControl('CONFERENCE_CALL', {
        phoneNo: number,
        PhoneNo: number,
        number,
      });
    }
    if (!ok && !isCubeWebphoneConnector()) {
      ok = await this.iframeCallControl('CONFERENCE_CALL', {
        phoneNo: number,
        PhoneNo: number,
        number,
      });
    }
    if (ok) {
      this.conferenceThirdPartyPending = true;
      return true;
    }
    this.emitError('Conference failed — stay connected, resume if on hold, then try again');
    return false;
  }

  async mergeCall(): Promise<boolean> {
    if (!this.isOnConnectedCall()) {
      this.emitError('Merge is available during a connected call');
      return false;
    }
    let ok = await this.restAction(
      this.callingService.completeCubeConference({ ConfMode: 'Conference' }),
      'merge via REST',
      'Merge failed'
    );
    if (!ok && isCubeWebphoneConnector()) {
      ok = await this.iframeCallControl('COMPLETE_CONFERENCE', { confMode: 'Conference' });
    }
    if (ok) {
      this.conferenceThirdPartyPending = false;
      return true;
    }
    this.emitError('Merge failed — Cube webphone not ready');
    return false;
  }

  dispose(disposition = CUBE_DEFAULT_DISPOSITION, endUi = true): boolean {
    if (shouldUseCubeSdk()) {
      return this.disposeViaSdk(disposition, endUi);
    }
    if (!this.agentLoggedIn) {
      this.emitError('Cube webphone not logged in');
      return false;
    }
    let ok = false;
    if (this.connector?.dispose) {
      ok = this.connector.dispose(disposition);
    }
    if (!ok) {
      ok = this.sendIframeAction('NEXT_CALL', { disposition });
    }
    if (ok && endUi) {
      void this.finishCallDispose(disposition);
    }
    return ok;
  }

  setAutoAnswer(enabled: boolean): boolean {
    if (shouldUseCubeSdk()) {
      this.lastAutoAnswerEnabled = enabled;
      return this.cubeSdk.setAutoAnswer(enabled);
    }
    if (this.lastAutoAnswerEnabled === enabled) {
      return true;
    }
    this.lastAutoAnswerEnabled = enabled;
    if (!this.connector) {
      return false;
    }
    if (this.connector.setAutoAnswer) {
      return this.connector.setAutoAnswer(enabled);
    }
    return this.sendIframeAction('AUTO_ANSWER', { autoAnswer: enabled });
  }

  setAutoCallMode(): boolean {
    if (!this.agentLoggedIn) {
      this.emitError('Cube webphone not logged in');
      return false;
    }
    return this.sendIframeAction('AUTO_CALL');
  }

  setManualCallMode(): boolean {
    if (!this.agentLoggedIn) {
      this.emitError('Cube webphone not logged in');
      return false;
    }
    return this.sendIframeAction('MANUAL_CALL');
  }

  nextCall(disposition = CUBE_DEFAULT_DISPOSITION): boolean {
    return this.dispose(disposition, true);
  }

  async breakStart(breakInfo?: string): Promise<boolean> {
    if (shouldUseCubeSdk()) {
      return this.breakStartViaSdk(breakInfo);
    }
    if (!this.agentLoggedIn) {
      this.emitError('Cube webphone not logged in');
      return false;
    }
    const info = resolveCubeBreakType(breakInfo || 'Tea Break');
    const cfg = getCubeBreakConfig(info);
    const breakPayload = {
      breakInfo: info,
      breakType: info,
      BreakType: info,
      BreakDurn: cfg.maxBreakTime,
      MaxBreakTime: cfg.maxBreakTime,
      NotifyBreak: cfg.notifyOnExceed,
      NotifyOnExceed: cfg.notifyOnExceed,
      IsWorkBreak: cfg.workBreak,
      WorkBreak: cfg.workBreak,
      maxBreakTime: cfg.maxBreakTime,
      notifyOnExceed: cfg.notifyOnExceed,
      workBreak: cfg.workBreak,
    };
    this.userBreakArmed = true;
    this.autoClearBreakUntil = 0;
    this.setUiStatus('on_break');
    this.publishCallState({ Status: 'Break', pauseCall: true, breakValue: info });

    const iframeOk = this.sendIframeAction('BREAK', breakPayload);
    if (iframeOk) {
      this.connector?.breakStart?.(info);
      return true;
    }

    let ok = this.connector?.breakStart?.(info) ?? false;
    if (!ok && (await this.hasRestAuthId())) {
      ok = await this.restAction(
        this.callingService.pauseCubeCall({ breakType: info }),
        'break via REST',
        'Break failed'
      );
    }
    if (!ok) {
      this.userBreakArmed = false;
      this.setUiStatus('ready');
      this.publishCallState({ Status: 'Idle', pauseCall: false, breakValue: '' });
    }
    return ok;
  }

  async breakEnd(): Promise<boolean> {
    if (shouldUseCubeSdk()) {
      return this.breakEndViaSdk();
    }
    if (!this.agentLoggedIn) {
      this.emitError('Cube webphone not logged in');
      return false;
    }
    const onBreak =
      this.getCurrentStatus() === 'on_break' ||
      !!this.callState$.getValue()?.pauseCall ||
      this.callState$.getValue()?.Status === 'Break' ||
      this.userBreakArmed;
    if (!onBreak) {
      return true;
    }
    const iframeOk = this.sendIframeAction('BREAK_END');
    let ok = iframeOk;
    if (!ok) {
      ok = this.connector?.breakEnd() ?? false;
    } else {
      this.connector?.breakEnd?.();
    }
    if (!ok && (await this.hasRestAuthId())) {
      ok = await this.restAction(
        this.callingService.resumeCubeCall(),
        'break end via REST',
        'Failed to end break'
      );
    }
    if (ok) {
      this.userBreakArmed = false;
      if (this.isOnActiveCall()) {
        this.setUiStatus('on_call');
        this.publishCallState({ pauseCall: false, breakValue: '' });
      } else {
        this.setUiStatus('ready');
        this.publishCallState({ Status: 'Idle', pauseCall: false, breakValue: '' });
      }
    }
    return ok;
  }

  answer(): boolean {
    if (shouldUseCubeSdk()) {
      return this.answerViaSdk();
    }
    if (!this.agentLoggedIn) {
      this.emitError('Cube webphone not logged in');
      return false;
    }
    if (this.cubeTransferRouting || this.isDummyAutoTransferSession()) {
      return false;
    }
    if (!getCubeAutoAnswerEnabled()) {
      this.setAutoAnswer(false);
    }
    if (this.callLegPhase === 'idle') {
      this.beginCallLeg(this.resolveCallLegSource('inbound'));
    }
    this.agentAnsweredByUser = true;
    this.cubeAwaitingAgentAnswer = false;
    this.cubeIframeAgentConnected = true;
    this.setUiStatus('on_call');
    const ok =
      this.sendIframeAction('ANSWER') ||
      this.sendIframeAction('ACCEPT_CALL') ||
      this.sendIframeAction('ANSWER_CALL');
    setTimeout(() => this.sendIframeAction('ANSWER'), 300);
    setTimeout(() => this.sendIframeAction('ANSWER'), 900);
    const state = this.callState$.getValue();
    const meta = this.extractCallMeta({ ...(state || {}), type: 'ANSWER' });
    this.publishCallState({
      Status: 'CONNECTED',
      customerAnswered: true,
      callDirection: this.callSource === 'campaign' ? 'campaign' : state?.callDirection,
      ...meta,
    });
    this.emit(this.callAnswered$, { type: 'ANSWER', ...meta });
    this.ensureOpenCaseFromConnected(meta);
    if (!ok) {
      if (getCubeAutoAnswerEnabled()) {
        this.scheduleAnswerConnectFallback();
        return true;
      }
      this.agentAnsweredByUser = false;
      this.cubeAwaitingAgentAnswer = true;
      this.publishCallState({ Status: 'CALLING', customerAnswered: true, ...meta });
      this.emitError('Failed to answer — Cube webphone did not accept answer command');
      return false;
    }
    this.scheduleAnswerConnectFallback();
    return true;
  }

  isInitialized(): boolean {
    if (shouldUseCubeSdk()) {
      return this.cubeSdk.isInitialized();
    }
    return !!this.connector;
  }

  isSoftphoneReady(): boolean {
    return this.softphoneReady;
  }

  isAgentLoggedIn(): boolean {
    return this.agentLoggedIn;
  }

  isLoginInFlight(): boolean {
    return this.loginInFlight;
  }

  isCubeSessionReady(): boolean {
    return (
      this.agentLoggedIn &&
      this.softphoneReady &&
      this.getCurrentStatus() !== 'failed' &&
      this.getCurrentStatus() !== 'logged_out' &&
      this.getCurrentStatus() !== 'on_break' &&
      !this.isAgentOnBreak()
    );
  }

  getPendingLeadDisplayMeta(phoneNo?: string): Partial<CubeCallUiState> {
    return this.resolvePendingLeadMeta(phoneNo);
  }

  private applyLookedUpCallerDetails(caseData: CubeCallUiState | null | undefined): void {
    if (!caseData) {
      return;
    }
    const fullName = this.cubeDialerFlow.resolveFullName(caseData);
    const phoneNo = this.cubeDialerFlow.getPhoneNumber(caseData) || this.callState$.getValue()?.phoneNo;
    if (phoneNo && fullName) {
      this.rememberCallerName(phoneNo, fullName);
    }
    if (!fullName && !caseData.dueId && !caseData.clientId) {
      return;
    }
    this.publishCallState({
      ...(fullName ? { fullName } : {}),
      ...(caseData.dueId ? { dueId: caseData.dueId } : {}),
      ...(caseData.clientId ? { clientId: caseData.clientId } : {}),
      ...(phoneNo ? { phoneNo } : {}),
    });
    const live = this.callState$.getValue();
    if (live) {
      this.incomeCallService.setCallingRes({
        ...live,
        ...(fullName ? { fullName } : {}),
        callingType: 'cube',
        Status: live.Status || 'CONNECTED',
      });
    }
  }

  private ensureOpenCaseFromConnected(meta: Partial<CubeCallUiState> = {}): void {
    if (isCubeAutoTransferDummySession()) {
      return;
    }
    const live = this.callState$.getValue();
    const leadMeta = this.resolvePendingLeadMeta(meta.phoneNo || live?.phoneNo);
    const openState = {
      ...(live || {}),
      ...leadMeta,
      ...meta,
      callingType: 'cube' as const,
      Status: 'CONNECTED' as const,
      customerAnswered: true,
      callDirection:
        this.callSource === 'campaign' ? 'campaign' : live?.callDirection || meta.callDirection,
    };
    if (!openState.phoneNo && !openState.dueId && !openState.clientId) {
      return;
    }
    this.incomeCallService.setCallingRes({ ...openState, callingType: 'cube', Status: 'CONNECTED' });
    void this.cubeDialerFlow.openCaseFromCall(openState, 'CONNECTED').then((caseData) => {
      this.applyLookedUpCallerDetails(caseData);
    });
  }

  logout(): boolean {
    if (shouldUseCubeSdk()) {
      this.agentLoggedIn = false;
      this.loginInFlight = false;
      this.clearLoginFlightTimer();
      this.clearLoginAckTimer();
      this.loginState$.next(false);
      this.setUiStatus('logged_out');
      return this.cubeSdk.logout();
    }
    this.agentLoggedIn = false;
    this.loginInFlight = false;
    this.clearLoginFlightTimer();
    this.clearLoginAckTimer();
    this.loginState$.next(false);
    this.setUiStatus('logged_out');
    const viaSdk = this.connector?.logout?.() ?? false;
    const viaIframe = this.sendIframeAction('LOGOUT', { force: true });
    return viaSdk || viaIframe;
  }

  teardownForUnload(): void {
    if (this.teardownDone) {
      return;
    }
    if (shouldUseCubeSdk()) {
      this.teardownViaSdk();
      return;
    }
    this.teardownDone = true;
    this.clearLoginFlightTimer();
    this.clearLoginAckTimer();
    this.clearConnectTimer();
    this.clearLoginRequestTimer();
    this.clearLoginFallbackTimer();
    this.clearLoginStatePoll();
    if (this.connector) {
      try {
        this.connector.logout();
      } catch (_e) { }
      try {
        this.connector.destroy();
      } catch (_e) { }
      this.connector = null;
    }
    this.removeMessageBridge();
    this.agentLoggedIn = false;
    this.loginInFlight = false;
    this.softphoneReady = false;
    this.preLoginLogoutDone = false;
    this.loginState$.next(false);
    this.setUiStatus('initializing');
    this.callState$.next(null);
  }

  destroy(): void {
    this.teardownForUnload();
  }

  /**
   * Widget Cube session. Cube only accepts calls after this exact order:
   * Initialize CubeConnector → login() → breakEnd() (login break) → makeCall / hold / etc.
   */
  private async initViaSdk(iframeId: string): Promise<void> {
    this.teardownDone = false;
    this.duplicateSessionRetryCount = 0;
    this.lastLoginError = '';
    this.setUiStatus('loading_webphone');
    let agent = await this.resolveAgentConfig(false);
    if (!isCubeAgentConfigComplete(agent)) {
      agent = await this.resolveAgentConfig(true);
    }
    if (!isCubeAgentConfigComplete(agent)) {
      const missing = describeMissingCubeCredentials();
      throw new Error(missing.length ? missing.join(', ') : 'Cube credentials not found in DB');
    }
    this.installUploadDialerWatch();
    this.wireSdkWidgetOnce();
    await this.cubeSdk.init(iframeId);
    this.cubeSdk.setAgent({
      agentId: agent.agentId,
      extension: agent.extension,
      password: agent.password,
      campaignId: agent.campaignId,
    });
    if (!this.softphoneReady) {
      try {
        await this.waitForSdkEvent(this.cubeSdk.ready$, 20000);
      } catch (_e) {}
      this.softphoneReady = true;
    }
    if (!this.agentLoggedIn) {
      const loggedIn = this.waitForSdkEvent(this.cubeSdk.loggedIn$, 20000);
      const ok = await this.loginViaSdk(true);
      if (ok) {
        try {
          await loggedIn;
        } catch (_e) {}
      }
    }
    if (this.agentLoggedIn && !this.userBreakArmed) {
      // Cube puts the agent on a login break; calls fail until breakEnd.
      this.cubeSdk.breakEnd();
      await new Promise((r) => setTimeout(r, 500));
      this.cubeSdk.breakEnd();
      this.setUiStatus('ready');
      this.publishCallState({ Status: 'Idle', pauseCall: false, breakValue: '' });
    }
  }

  private waitForSdkEvent(subject: Subject<unknown>, timeoutMs: number): Promise<unknown> {
    return new Promise((resolve, reject) => {
      const sub = subject.subscribe((value) => {
        clearTimeout(timer);
        sub.unsubscribe();
        resolve(value);
      });
      const timer = setTimeout(() => {
        sub.unsubscribe();
        reject(new Error('timeout'));
      }, timeoutMs);
    });
  }

  /** Extra breakEnd after onLoggedIn — Cube's login-break event can arrive late. */
  private scheduleSdkBreakEndAfterLogin(): void {
    [400, 1200].forEach((ms) => {
      setTimeout(() => {
        if (!this.agentLoggedIn || this.userBreakArmed) {
          return;
        }
        this.cubeSdk.breakEnd();
      }, ms);
    });
  }

  /** Subscribe widget state to CubeSdkService subjects so debug and widget share one session. */
  private wireSdkWidgetOnce(): void {
    if (this.sdkWidgetWired) {
      return;
    }
    this.sdkWidgetWired = true;
    this.cubeSdk.ready$.subscribe((data) => {
      this.softphoneReady = true;
      this.emit(this.ready$, data);
    });
    this.cubeSdk.loggedIn$.subscribe((data) => this.markLoggedIn(data));
    this.cubeSdk.loginFailed$.subscribe((data) => this.markLoginFailed(data));
    this.cubeSdk.loggedOut$.subscribe((data) => this.markLoggedOut(data));
    this.cubeSdk.inboundCall$.subscribe((data) => {
      this.emit(this.inboundCall$, data);
      this.callSource = this.resolveInboundCallSource(data);
      if (this.isDummyAutoTransferSession()) {
        this.callSource = 'campaign';
      }
      this.primeAgentAnswerRing(data);
      this.handleCallProgress(data, 'CALLING');
    });
    this.cubeSdk.callAnswered$.subscribe((data) => this.handleSdkCallAnswered(data));
    this.cubeSdk.callEnded$.subscribe((data) => this.handleSdkCallEnded(data));
    this.cubeSdk.callFailed$.subscribe((data) => this.handleSdkCallEnded(data));
    this.cubeSdk.message$.subscribe((data) => this.handleSdkMessage(data));
    this.cubeSdk.breakStarted$.subscribe((data) => {
      this.emit(this.breakStarted$, data);
      const incomingBreak = this.extractIncomingBreakLabel(data);
      const loginBreak = isCubeLoginBreak(incomingBreak) || !incomingBreak;
      if (loginBreak && !this.userBreakArmed) {
        this.setUiStatus('on_break');
        this.publishCallState({
          Status: 'Break',
          pauseCall: true,
          breakValue: incomingBreak || 'Login Break',
        });
        this.cubeSdk.breakEnd();
        return;
      }
      if (this.userBreakArmed || isCubeBreakType(incomingBreak)) {
        this.userBreakArmed = true;
        this.autoClearBreakUntil = 0;
      }
      this.setUiStatus('on_break');
      const armed =
        this.callState$.getValue()?.breakValue ||
        (isCubeBreakType(incomingBreak) ? incomingBreak : '');
      const breakValue =
        this.userBreakArmed && isCubeLoginBreak(incomingBreak)
          ? resolveCubeBreakType(armed || 'Tea Break')
          : resolveCubeBreakType(armed || incomingBreak || 'Tea Break');
      this.publishCallState({ Status: 'Break', pauseCall: true, breakValue });
    });
    this.cubeSdk.breakEnded$.subscribe((data) => {
      this.emit(this.breakEnded$, data);
      const endVia = String((data as { via?: string })?.via || '');
      if (this.userBreakArmed && (endVia === 'detect' || endVia === 'reassert' || endVia === 'BREAK_END_BLOCKED')) {
        return;
      }
      this.userBreakArmed = false;
      if (this.agentLoggedIn) {
        this.setUiStatus('ready');
      }
    });
    this.cubeSdk.error$.subscribe((data) => {
      this.handleSdkError(data);
    });
  }

  /** Manual outbound has no separate agent-answer step; Cube onCallAnswered means the call is live. */
  private handleSdkCallAnswered(data: unknown): void {
    this.emit(this.callAnswered$, data);
    if (this.callEndInProgress || this.callLegCancelled) {
      this.tearDownCallLeg();
      return;
    }
    const isManual = this.callSource === 'manual' || this.isManualDialerCall();
    if (!this.agentAnsweredByUser && !isManual && !getCubeAutoAnswerEnabled()) {
      this.cubeAwaitingAgentAnswer = true;
      this.handleCallProgress(data, 'CALLING');
      return;
    }
    this.agentAnsweredByUser = true;
    this.cubeAwaitingAgentAnswer = false;
    this.cubeIframeAgentConnected = true;
    this.handleCallProgress({ ...(data as object), type: 'CALL_ANSWERED' }, 'CONNECTED');
  }

  /**
   * Customer hangup (including while on hold) must enter wrap-up.
   * iframe handleRemoteCallEnd treats some of these as stale idle and would leave hold stuck.
   */
  private handleSdkCallEnded(data: unknown): void {
    const row = data && typeof data === 'object' ? (data as Record<string, unknown>) : {};
    const type = String(row['type'] || row['event'] || 'CALL_ENDED').toUpperCase();
    const held = this.isCallOnHold();
    const live =
      held ||
      this.isOnActiveCall() ||
      this.outboundDialSent ||
      this.callLegPhase === 'active' ||
      this.status$.getValue() === 'on_call';
    if (!this.callLegEverConnected && live) {
      this.callLegEverConnected = true;
    }
    if (held) {
      this.publishCallState({ holdCall: false });
    }
    if (live && this.callLegPhase !== 'wrap_up' && !this.cubeWrapUpPending) {
      this.beginWrapUp({
        ...row,
        type: type === 'WRAP_UP' || type === 'WRAPUP' ? 'WRAP_UP' : 'CALL_ENDED',
        reason: 'remote',
        snapshot: this.callState$.getValue(),
      });
      return;
    }
    this.handleRemoteCallEnd({
      ...row,
      type: type === 'WRAP_UP' || type === 'WRAPUP' ? 'WRAP_UP' : 'CALL_ENDED',
      reason: String(row['reason'] || 'remote'),
    });
  }

  /** Cube sometimes sends hangup as onMessage (WRAP_UP / IDLE) instead of onCallEnded. */
  private handleSdkMessage(data: unknown): void {
    const row = data && typeof data === 'object' ? (data as Record<string, unknown>) : {};
    const nested =
      row['payload'] && typeof row['payload'] === 'object'
        ? (row['payload'] as Record<string, unknown>)
        : row;
    const type = String(nested['type'] || nested['event'] || row['type'] || row['event'] || '').toUpperCase();
    if (
      type === 'CALL_ENDED' ||
      type === 'CALL_END' ||
      type === 'HANGUP' ||
      type === 'CALL_DISCONNECTED' ||
      type === 'DISCONNECT' ||
      type === 'CALL_DROP' ||
      type === 'DROP' ||
      type === 'CUSTOMER_HANGUP' ||
      type === 'REMOTE_HANGUP' ||
      type === 'WRAP_UP' ||
      type === 'WRAPUP' ||
      (this.isCallOnHold() && (type === 'IDLE' || type === 'FREEME' || type === 'AGENT_IDLE'))
    ) {
      this.handleSdkCallEnded({ ...nested, ...row, type });
      return;
    }
    if (type === 'CALL_ANSWERED' || type === 'ANSWERED' || type === 'CONNECTED') {
      this.handleSdkCallAnswered({ ...nested, ...row, type });
    }
  }

  private extractSdkErrorText(data: unknown): string {
    if (typeof data === 'string') {
      return data;
    }
    const row = data && typeof data === 'object' ? (data as Record<string, unknown>) : {};
    return String(
      row['message'] ||
        row['error'] ||
        row['reason'] ||
        row['resultLong'] ||
        row['type'] ||
        ''
    );
  }

  /** breakStart() only means the request was sent; onError must revert the widget break UI. */
  private handleSdkError(data: unknown): void {
    const msg = this.extractSdkErrorText(data) || (typeof data === 'string' ? data : JSON.stringify(data));
    if (
      (this.isCallOnHold() || this.isOnActiveCall()) &&
      /hang\s*up|disconnect|ended|drop|customer/i.test(msg)
    ) {
      this.handleSdkCallEnded({ type: 'CALL_ENDED', reason: 'remote', error: msg });
      return;
    }
    this.emitError(msg);
    if (!this.userBreakArmed || Date.now() - this.sdkBreakStartAt > 8000) {
      return;
    }
    this.userBreakArmed = false;
    this.emit(this.breakEnded$, { reason: 'error', error: msg });
    if (this.cubeWrapUpPending || this.callLegPhase === 'wrap_up') {
      return;
    }
    if (this.isOnActiveCall()) {
      this.publishCallState({ pauseCall: false, breakValue: '' });
      this.setUiStatus('on_call');
      return;
    }
    this.setUiStatus('ready');
    this.publishCallState({ Status: 'Idle', pauseCall: false, breakValue: '' });
  }

  private async loginViaSdk(force = false): Promise<boolean> {
    if (this.agentLoggedIn && !force) {
      return true;
    }
    if (!this.cubeSdk.isInitialized()) {
      return false;
    }
    if (this.loginInFlight && !force) {
      return false;
    }
    const agent = await this.resolveAgentConfig(force);
    if (!isCubeAgentConfigComplete(agent)) {
      const missing = describeMissingCubeCredentials();
      this.emitError(
        missing.length
          ? `Cube login credentials missing: ${missing.join(', ')}`
          : 'Cube agent credentials not found. Re-login to Moneytor.'
      );
      return false;
    }
    this.cubeSdk.setAgent({
      agentId: agent.agentId,
      extension: agent.extension,
      password: agent.password,
      campaignId: agent.campaignId,
    });
    this.loginInFlight = true;
    this.setUiStatus('logging_in');
    const ok = this.cubeSdk.login();
    if (!ok) {
      this.loginInFlight = false;
      this.setUiStatus('failed');
      this.emitError('Cube login failed');
    }
    return ok;
  }

  private async forceLoginViaSdk(): Promise<boolean> {
    this.agentLoggedIn = false;
    this.loginInFlight = false;
    this.clearLoginFlightTimer();
    this.clearLoginAckTimer();
    this.clearLoginRequestTimer();
    this.clearLoginFallbackTimer();
    this.duplicateSessionRetryCount = 0;
    this.lastLoginError = '';
    this.callSource = null;
    this.agentAnsweredByUser = false;
    this.cubeAwaitingAgentAnswer = false;
    this.callLegCancelled = false;
    this.callEndInProgress = false;
    this.callDisposeInFlight = false;
    this.pendingCampaignMeta = {};
    this.publishCallState({
      Status: 'Idle',
      customerAnswered: false,
      phoneNo: '',
      holdCall: false,
      pauseCall: false,
      callDirection: undefined,
    });
    this.loginState$.next(false);
    this.setUiStatus('logging_in');
    this.cubeSdk.logout();
    return this.loginViaSdk(true);
  }

  private makeCallViaSdk(phoneNumber: string, context?: Partial<CubeCallUiState>): boolean {
    if (!this.cubeSdk.isInitialized() && !document.getElementById(getCubeBridgeIframeId())) {
      this.emitError('Cube webphone not initialized');
      return false;
    }
    if (!this.agentLoggedIn) {
      this.emitError('Cube webphone not logged in');
      return false;
    }
    const number = String(phoneNumber || '').replace(/\D/g, '').slice(-10);
    if (!number || number.length < 10) {
      this.emitError('Invalid phone number');
      return false;
    }
    this.callEndInProgress = false;
    this.callLegCancelled = false;
    this.cubeIframeIdleTicks = 0;
    this.cubeWrapUpPending = false;
    const isManual = (context as { callDirection?: string })?.callDirection !== 'campaign';
    this.beginCallLeg(isManual ? 'manual' : 'campaign');
    this.agentAnsweredByUser = false;
    this.cubeIframeAgentConnected = false;
    this.callLegEverConnected = false;
    this.outboundDialSent = false;
    this.cubeAwaitingAgentAnswer = !isManual;
    if (isManual) {
      this.clearAutoAnswerPromoteTimer();
      this.clearConnectTimer();
    }
    this.publishCallState({
      Status: 'CALLING',
      phoneNo: number,
      phoneNumber: number,
      callDirection: isManual ? 'manual' : 'campaign',
      customerAnswered: false,
      ...(context || {}),
    });
    if (context?.fullName) {
      this.rememberCallerName(number, context.fullName);
    }
    if (context?.clientId) {
      localStorage.setItem('clientId', String(context.clientId));
    }
    localStorage.setItem('incomingCall', 'true');
    try {
      localStorage.setItem('callingType', 'cube');
    } catch (_e) {}
    this.incomeCallService.setCallingRes({
      ...this.callState$.getValue(),
      callingType: 'cube',
      Status: 'CALLING',
      phoneNo: number,
      phoneNumber: number,
      fullName: context?.fullName || this.callState$.getValue()?.fullName || '',
    });
    this.setUiStatus('on_call');
    void this.placeOutboundCallViaSdk(number);
    return true;
  }

  private async placeOutboundCallViaSdk(number: string): Promise<boolean> {
    this.cubeSdk.breakEnd();
    await new Promise((r) => setTimeout(r, 400));
    const ok = this.cubeSdk.makeCall(number);
    if (!ok) {
      this.emitError('Failed to place call');
      this.endCallLegToIdle();
      this.cubeAwaitingAgentAnswer = false;
      if (this.agentLoggedIn) {
        this.setUiStatus('ready');
      }
      this.ngZone.run(() => {
        this.callState$.next({
          callingType: 'cube',
          Status: 'Idle',
          holdCall: false,
          customerAnswered: false,
          phoneNo: '',
        });
      });
      return false;
    }
    this.outboundDialSent = true;
    return true;
  }

  private async holdCallViaSdk(): Promise<boolean> {
    if (this.callControlInFlight) {
      return false;
    }
    if (!this.isOnActiveCall()) {
      this.emitError('Hold is available during an active call');
      return false;
    }
    this.callControlInFlight = true;
    try {
      const ok = this.cubeSdk.hold();
      if (ok) {
        this.publishCallState({ holdCall: true });
      } else {
        this.emitError('Failed to hold call');
      }
      return ok;
    } finally {
      this.callControlInFlight = false;
    }
  }

  private async unholdCallViaSdk(): Promise<boolean> {
    if (this.callControlInFlight) {
      return false;
    }
    if (!this.isOnActiveCall()) {
      this.emitError('No active call to resume');
      return false;
    }
    this.callControlInFlight = true;
    try {
      const ok = this.cubeSdk.unhold();
      if (ok) {
        this.publishCallState({ holdCall: false });
      } else {
        this.emitError('Failed to resume call');
      }
      return ok;
    } finally {
      this.callControlInFlight = false;
    }
  }

  private async muteViaSdk(): Promise<boolean> {
    if (!this.isOnActiveCall()) {
      this.emitError('Mute is available during an active call');
      return false;
    }
    const ok = this.cubeSdk.mute();
    if (!ok) {
      this.emitError('Failed to mute call');
    }
    return ok;
  }

  private async unmuteViaSdk(): Promise<boolean> {
    if (!this.isOnActiveCall()) {
      return false;
    }
    const ok = this.cubeSdk.unmute();
    if (!ok) {
      this.emitError('Failed to unmute call');
    }
    return ok;
  }

  /** SDK path: CubeConnector.redirect(agentId) — dummy → real auto-transfer and widget Transfer. */
  private async redirectViaSdk(
    target: string,
    options?: { extension?: string; campaignId?: string; allowOffering?: boolean }
  ): Promise<boolean> {
    const value = String(target || '').trim();
    this.cubeLog('redirect', { target: value, via: 'sdk', allowOffering: !!options?.allowOffering });
    console.log('[CubeAutoTransfer] cubeSdk.redirect()', {
      target: value,
      campaignId: options?.campaignId || '',
      allowOffering: !!options?.allowOffering,
    });
    if (!value) {
      this.emitError('Enter agent ID to redirect to');
      return false;
    }
    if (this.cubeTransferInFlight) {
      return false;
    }
    this.cubeTransferInFlight = true;
    try {
      const state = this.callState$.getValue();
      const connected = this.isOnConnectedCall();
      const blindOffer =
        !!options?.allowOffering &&
        !connected &&
        (this.cubeAwaitingAgentAnswer ||
          (state?.Status === 'CALLING' && !!state?.customerAnswered));
      if (!connected && !blindOffer) {
        console.log('[CubeAutoTransfer] cubeSdk.redirect() skipped — not connected / not blind-offer', {
          target: value,
          status: state?.Status,
          allowOffering: !!options?.allowOffering,
        });
        this.emitError('Redirect is available during a connected call');
        return false;
      }
      if (!options?.allowOffering && this.isCallOnHold()) {
        const resumed = await this.unholdCallViaSdk();
        if (!resumed) {
          this.emitError('Resume the call before redirect');
          return false;
        }
      }
      const ok = this.cubeSdk.redirect(value);
      console.log('[CubeAutoTransfer] cubeSdk.redirect() result', { target: value, ok });
      if (ok) {
        this.cubeTransferDispatched = true;
      }
      return ok;
    } finally {
      this.cubeTransferInFlight = false;
    }
  }

  /** Widget Dispose button while wrap-up is open — same CubeConnector.dispose() as submitWrapUpDisposition. */
  private disposeViaSdk(disposition: string, endUi: boolean): boolean {
    if (!this.agentLoggedIn) {
      this.emitError('Cube webphone not logged in');
      return false;
    }
    const ok = this.cubeSdk.dispose(disposition, null, '0');
    if (!ok) {
      this.emitError('Failed to dispose call');
      return false;
    }
    if (endUi) {
      void this.completeSdkDispose(disposition);
    }
    return true;
  }

  private async completeSdkDispose(disposition: string): Promise<void> {
    const state = this.callState$.getValue();
    try {
      await firstValueFrom(
        this.callingService.disposeCubeCallRaphsody({
          clientId: state?.clientId || '',
          Disposition: disposition,
        })
      );
    } catch (_e) {}
    void this.bootstrapRestAuthId();
    this.cubeWrapUpPending = false;
    this.endCallLegToIdle();
    this.notifyCallEnded('disposition');
  }

  /** Optimistic break UI; handleSdkError reverts if Cube rejects. */
  private async breakStartViaSdk(breakInfo?: string): Promise<boolean> {
    if (!this.agentLoggedIn) {
      this.emitError('Cube webphone not logged in');
      return false;
    }
    const info = resolveCubeBreakType(breakInfo || 'Tea Break');
    this.userBreakArmed = true;
    this.sdkBreakStartAt = Date.now();
    this.autoClearBreakUntil = 0;
    this.setUiStatus('on_break');
    this.publishCallState({ Status: 'Break', pauseCall: true, breakValue: info });
    const ok = this.cubeSdk.breakStart(info);
    if (!ok) {
      this.userBreakArmed = false;
      this.setUiStatus('ready');
      this.publishCallState({ Status: 'Idle', pauseCall: false, breakValue: '' });
    }
    return ok;
  }

  private async breakEndViaSdk(): Promise<boolean> {
    if (!this.agentLoggedIn) {
      this.emitError('Cube webphone not logged in');
      return false;
    }
    const ok = this.cubeSdk.breakEnd();
    if (ok) {
      this.userBreakArmed = false;
      if (this.isOnActiveCall()) {
        this.publishCallState({ pauseCall: false, breakValue: '' });
      } else {
        this.setUiStatus('ready');
        this.publishCallState({ Status: 'Idle', pauseCall: false, breakValue: '' });
      }
    }
    return ok;
  }

  private answerViaSdk(): boolean {
    if (!this.agentLoggedIn) {
      this.emitError('Cube webphone not logged in');
      return false;
    }
    if (this.cubeTransferRouting || this.isDummyAutoTransferSession()) {
      return false;
    }
    if (!getCubeAutoAnswerEnabled()) {
      this.setAutoAnswer(false);
    }
    if (this.callLegPhase === 'idle') {
      this.beginCallLeg(this.resolveCallLegSource('inbound'));
    }
    this.agentAnsweredByUser = true;
    this.cubeAwaitingAgentAnswer = false;
    this.cubeIframeAgentConnected = true;
    this.setUiStatus('on_call');
    const ok = this.cubeSdk.answer();
    const state = this.callState$.getValue();
    const meta = this.extractCallMeta({ ...(state || {}), type: 'ANSWER' });
    this.publishCallState({
      Status: 'CONNECTED',
      customerAnswered: true,
      callDirection: this.callSource === 'campaign' ? 'campaign' : state?.callDirection,
      ...meta,
    });
    this.emit(this.callAnswered$, { type: 'ANSWER', ...meta });
    this.ensureOpenCaseFromConnected(meta);
    if (!ok) {
      if (getCubeAutoAnswerEnabled()) {
        this.scheduleAnswerConnectFallback();
        return true;
      }
      this.agentAnsweredByUser = false;
      this.cubeAwaitingAgentAnswer = true;
      this.publishCallState({ Status: 'CALLING', customerAnswered: true, ...meta });
      this.emitError('Failed to answer');
      return false;
    }
    this.scheduleAnswerConnectFallback();
    return true;
  }

  private teardownViaSdk(): void {
    this.teardownDone = true;
    this.clearLoginFlightTimer();
    this.clearLoginAckTimer();
    this.clearConnectTimer();
    this.clearLoginRequestTimer();
    this.clearLoginFallbackTimer();
    this.clearLoginStatePoll();
    this.cubeSdk.destroy();
    this.agentLoggedIn = false;
    this.loginInFlight = false;
    this.softphoneReady = false;
    this.preLoginLogoutDone = false;
    this.loginState$.next(false);
    this.setUiStatus('initializing');
    this.callState$.next(null);
  }
}
